import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { v4 as uuidv4 } from 'uuid';

const SAFForm = ({ userData }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [event, setEvent] = useState(null);
  const [availableSchools, setAvailableSchools] = useState([]);
  const [availableTerritories, setAvailableTerritories] = useState([]);
  const [availableInstructors, setAvailableInstructors] = useState([]);
  const [availableConcentrations, setAvailableConcentrations] = useState([]);
  const [reviewer, setReviewer] = useState(null);
  const [availableSubcalendars, setAvailableSubcalendars] = useState([]);
  const [availableOfficialPartners, setAvailableOfficialPartners] = useState([]);
  const [showCreatePartnerModal, setShowCreatePartnerModal] = useState(false);
  const [newPartnerData, setNewPartnerData] = useState({ title: '', displayname: '' });
  const [creatingPartner, setCreatingPartner] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [mode, setMode] = useState(null); // leadinstructor | coordinator | trainingplan | privileged
  const [trainingPlanId, setTrainingPlanId] = useState(null);
  const [trainingPlan, setTrainingPlan] = useState(null);
  const [trainingPlanLoading, setTrainingPlanLoading] = useState(false);
  const [trainingPlanError, setTrainingPlanError] = useState('');
  const [trainingPlanFile, setTrainingPlanFile] = useState(null);
  const [trainingPlanUploading, setTrainingPlanUploading] = useState(false);
  const [trainingPlanSubmitting, setTrainingPlanSubmitting] = useState(false);
  const [jobTemplates, setJobTemplates] = useState([]);
  const [jobsByEvent, setJobsByEvent] = useState([]);
  const [jobTemplateLoading, setJobTemplateLoading] = useState(false);
  const [jobsByEventLoading, setJobsByEventLoading] = useState(false);
  const [jobTemplateError, setJobTemplateError] = useState('');
  const [jobsByEventError, setJobsByEventError] = useState('');
  const [showJobTemplateForm, setShowJobTemplateForm] = useState(false);
  const [newJobTemplate, setNewJobTemplate] = useState({ title: '', description: '', otherConcentrationsEnabled: false, otherConcentrations: [] });
  const [proposeJobModal, setProposeJobModal] = useState(null); // { job, slots, title, description, otherConcentrationsEnabled, otherConcentrations }
  const [proposeJobError, setProposeJobError] = useState('');
  const [proposeJobLoading, setProposeJobLoading] = useState(false);
  const [editJobByEvent, setEditJobByEvent] = useState(null); // { ...jbe }
  const [editJobByEventError, setEditJobByEventError] = useState('');
  const [editJobByEventSaving, setEditJobByEventSaving] = useState(false);
  const [criticalError, setCriticalError] = useState(null);
  const [safAccess, setSafAccess] = useState({ canView: false, canEdit: false, role: null });

  // Training plans list state
  const [trainingPlansList, setTrainingPlansList] = useState([]);
  const [trainingPlansLoading, setTrainingPlansLoading] = useState(false);
  const [trainingPlansError, setTrainingPlansError] = useState('');
  const [showTrainingPlansTable, setShowTrainingPlansTable] = useState(false);
  const [selectedTrainingPlan, setSelectedTrainingPlan] = useState(null);
  const [showTrainingPlanModal, setShowTrainingPlanModal] = useState(false);

  // Hide sticky Save/Submit button when training plans table is visible
  const [isTrainingPlansInView, setIsTrainingPlansInView] = useState(false);
  const [showJobsNotification, setShowJobsNotification] = useState(false);
  
  // Store original form data to detect changes
  const [originalFormData, setOriginalFormData] = useState(null);

  // 1. Add jobsByEventForModal state
  const [jobsByEventForModal, setJobsByEventForModal] = useState([]);

  // State for confirmed jobs (for SAF approval validation)
  const [confirmedJobs, setConfirmedJobs] = useState([]);
  const [confirmedJobsLoading, setConfirmedJobsLoading] = useState(false);
  const [confirmedJobsError, setConfirmedJobsError] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    date: '',
    location: '',
    hoursAvailable: '',
    coverPhoto: '',
    startTime: '',
    endTime: '',
    description: '',
    registrationLimit: null,
    hasRegistrationLimit: false,
    concentrations: [],
    schools: [],
    territories: [],
    allowAllSchools: false,
    allowAllTerritories: false,
    teamUpId: '',
    subcalendarIds: [],
    type: 'Hybrid',
    leadInstructor: '',
    status: 'live',
    officialPartners: []
  });

  // Cover photo upload state
  const [coverPhotoUploading, setCoverPhotoUploading] = useState(false);
  const [coverPhotoUploadError, setCoverPhotoUploadError] = useState('');

  // Add state at the top:
  const [actionOverlay, setActionOverlay] = useState(false);
  const [showJobsIcon, setShowJobsIcon] = useState(false);

  // Auto-resize for Description textarea
  const descriptionRef = useRef(null);
  const autoResizeTextarea = useCallback((el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    try {
      const el = document.getElementById(sectionId);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const absoluteY = rect.top + window.pageYOffset;
      const centeredY = absoluteY - (window.innerHeight / 2 - rect.height / 2);
      const targetY = Math.max(centeredY, 0);
      window.scrollTo({ top: targetY, behavior: 'smooth' });

      el.classList.add('highlight-flash');
      setTimeout(() => {
        el.classList.remove('highlight-flash');
      }, 1500);
    } catch (e) {
      // no-op
    }
  }, []);

  const loadEventData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Load all required data in parallel
      const [eventResponse, schoolsResponse, territoriesResponse, instructorsResponse, concentrationsResponse, subcalendarsResponse, associationsResponse, safAccessResponse] = await Promise.all([
        axios.get(`/api/field-experience-events/${eventId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/field-experience-events/schools', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/field-experience-events/territories', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/field-experience-events/lead-instructors', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/concentrations', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/field-experience-events/teamup/subcalendars', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/user-management/associations', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`/api/field-experience-events/${eventId}/saf-access`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const eventData = eventResponse.data.event;
      const access = safAccessResponse.data || { canView: false, canEdit: false, role: null };
      setSafAccess(access);
      setEvent(eventData);
      setAvailableSchools(schoolsResponse.data.schools);
      setAvailableTerritories(territoriesResponse.data.territories);
      setAvailableInstructors(instructorsResponse.data.leadInstructors);
      setAvailableConcentrations(concentrationsResponse.data.concentrations);
      setAvailableSubcalendars(subcalendarsResponse.data.subcalendars || []);
      // Filter associations by type "Official Partner"
      const officialPartners = (associationsResponse.data.associations || []).filter(
        assoc => assoc.type === 'Official Partner'
      );
      setAvailableOfficialPartners(officialPartners);

      // Fetch training plans if stage is 'saf' or 'official'
      if (eventData.stage === 'saf' || eventData.stage === 'official') {
        await fetchTrainingPlansForEvent();
      }

      const urlMode = searchParams.get('mode');
      const urlTrainingPlanId = searchParams.get('trainingplan_id');
      let determinedMode = null;
      let reviewerData = null;

      if (urlMode === 'trainingplan') {
        determinedMode = 'trainingplan';
        setTrainingPlanId(urlTrainingPlanId);
      } else if (!access.canView && !access.canEdit) {
        setLoading(false);
        setCriticalError('Unable to determine your role for this event. You may not have the necessary permissions to access this SAF form.');
        return;
      } else if (access.role === 'leadinstructor') {
        determinedMode = 'leadinstructor';
      } else if (access.role === 'coordinator') {
        determinedMode = 'coordinator';
        try {
          const reviewerRes = await axios.get(`/api/field-experience-events/${eventId}/reviewer`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          reviewerData = reviewerRes.data.reviewer || null;
        } catch (err) {
          console.error('Error fetching reviewer:', err);
        }
      } else if (access.role === 'privileged') {
        determinedMode = 'privileged';
      }

      if (!determinedMode) {
        setLoading(false);
        setCriticalError('Unable to determine your role for this event. You may not have the necessary permissions to access this SAF form.');
        return;
      }

      setMode(determinedMode);
      if (reviewerData) {
        setReviewer(reviewerData);
      } else if (determinedMode === 'coordinator') {
        console.error('Coordinator mode set but reviewer data is missing');
        setError('Unable to load coordinator data. Please try again.');
      }

      console.log('SAF Form Mode:', determinedMode);
      if (determinedMode === 'trainingplan') {
        console.log('Training Plan ID:', urlTrainingPlanId);
      }

      // Convert schools and territories to strings to ensure proper comparison
      const schoolsArray = Array.isArray(eventData.schools)
        ? eventData.schools.map(s => s.toString())
        : [];
      const territoriesArray = Array.isArray(eventData.territories)
        ? eventData.territories.map(t => t.toString())
        : [];

      console.log('Converted schools array:', schoolsArray);
      console.log('Converted territories array:', territoriesArray);
      console.log('Concentrations from event (IDs):', eventData.concentrations);
      console.log('Available concentrations:', concentrationsResponse.data.concentrations);

      // Pre-fill form with existing data
      const formDataToSet = {
        title: eventData.title || '',
        date: eventData.date ? new Date(eventData.date).toISOString().split('T')[0] : '',
        location: eventData.location || '',
        hoursAvailable: eventData.hoursavailable || '',
        coverPhoto: eventData.coverphoto || '',
        startTime: eventData.start_time || '',
        endTime: eventData.end_time || '',
        description: eventData.description || '',
        registrationLimit: eventData.registrationlimit || null,
        hasRegistrationLimit: eventData.registrationlimit !== null,
        concentrations: eventData.concentrations || [],
        schools: schoolsArray,
        territories: territoriesArray,
        allowAllSchools: Boolean(eventData.allowallschools),
        allowAllTerritories: Boolean(eventData.allowallterritories),
        teamUpId: eventData.teamup_id || '',
        subcalendarIds: Array.isArray(eventData.subcalendar_ids)
          ? eventData.subcalendar_ids.map(id => id.toString())
          : (typeof eventData.subcalendar_ids === 'string' && eventData.subcalendar_ids.length > 0
            ? eventData.subcalendar_ids.split(',').map(id => id.trim())
            : []),
        type: eventData.type || 'Hybrid',
        leadInstructor: eventData.leadinstructor || '',
        status: eventData.stage === 'official' ? eventData.status || 'live' : 'live',
        officialPartners: Array.isArray(eventData.officialpartners)
          ? eventData.officialpartners.map(id => id.toString())
          : []
      };

      console.log('Form data to set:', formDataToSet);
      console.log('Registration limit from event:', eventData.registrationlimit);
      console.log('Has registration limit:', eventData.registrationlimit !== null);
      console.log('Concentrations from event:', eventData.concentrations);
      console.log('Available concentrations:', concentrationsResponse.data.concentrations);

      setFormData(formDataToSet);
      // Store original data for change detection
      setOriginalFormData(JSON.parse(JSON.stringify(formDataToSet)));

    } catch (error) {
      setCriticalError('Unable to load required event data (event, concentrations, schools, territories, or TeamUp calendars). Please try again later or contact your administrator.');
      setLoading(false);
      return;
    } finally {
      setLoading(false);
    }
  }, [eventId, userData.account_id, searchParams]);

  useEffect(() => {
    loadEventData();
  }, [eventId]);

  // Observe training plans section to hide sticky Save/Submit when in view
  useEffect(() => {
    const section = document.getElementById('training-plans-section');
    if (!section) {
      setIsTrainingPlansInView(false);
      return;
    }

    let wasInView = false;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        const isNowInView = entry.isIntersecting;
        setIsTrainingPlansInView(isNowInView);
        
        // Show notification only if the Save/Submit button was actually visible just before hiding
        const saveButtonWouldBeVisible = (mode !== 'trainingplan') && !wasInView && hasUnsavedChanges();
        if (isNowInView && !wasInView && saveButtonWouldBeVisible) {
          setShowJobsNotification(true);
          setTimeout(() => {
            setShowJobsNotification(false);
          }, 3000);
        }
        
        wasInView = isNowInView;
      },
      {
        root: null,
        threshold: 0.1
      }
    );

    observer.observe(section);

    return () => {
      try { observer.disconnect(); } catch (_) {}
    };
  }, [showTrainingPlansTable, mode, originalFormData, formData]);

  // Ensure Description textarea fits content whenever its value changes
  useEffect(() => {
    if (descriptionRef && descriptionRef.current) {
      autoResizeTextarea(descriptionRef.current);
    }
  }, [formData.description, autoResizeTextarea]);

  // Load training plan data when in trainingplan mode
  const loadTrainingPlan = useCallback(async () => {
    if (mode !== 'trainingplan' || !trainingPlanId) return;

    try {
      setTrainingPlanLoading(true);
      setTrainingPlanError('');
      const token = localStorage.getItem('token');

      const response = await axios.get(`/api/field-experience-events/training-plans/${trainingPlanId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const tp = response.data.trainingPlan;

      console.log('Training Plan Data Received:', tp);
      console.log('Concentration data:', tp.concentration);

      // Check if user is uninvited or not invited
      if (tp.uninvited === true) {
        setTrainingPlanError('You are no longer invited to this training plan.');
        setTrainingPlanLoading(false);
        return;
      }

      if (tp.status !== 'invite' && tp.status !== 'building' && tp.status !== 'submitted') {
        setTrainingPlanError('Invalid training plan status.');
        setTrainingPlanLoading(false);
        return;
      }

      setTrainingPlan(tp);

      // Scroll to bottom after a short delay to ensure form is rendered
      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth'
        });
      }, 500);

    } catch (error) {
      console.error('Error loading training plan:', error);
      setTrainingPlanError('Failed to load training plan data');
    } finally {
      setTrainingPlanLoading(false);
    }
  }, [mode, trainingPlanId]);

  // Load training plan when mode changes to trainingplan
  useEffect(() => {
    if (mode === 'trainingplan') {
      loadTrainingPlan();
    }
  }, [mode, trainingPlanId]);

  // Fetch job templates and jobsbyevent when training plan loads
  useEffect(() => {
    if (mode === 'trainingplan' && trainingPlan) {
      fetchJobTemplates();
      fetchJobsByEvent();
    }
  }, [mode, trainingPlan]);

  // Fetch jobs for selected training plan when modal opens
  useEffect(() => {
    if (showTrainingPlanModal && selectedTrainingPlan) {
      fetchJobsByEventForModal();
    }
  }, [showTrainingPlanModal, selectedTrainingPlan]);

  // Fetch confirmed jobs when event loads (for SAF approval validation)
  useEffect(() => {
    if (event && event.id && mode === 'coordinator') {
      fetchConfirmedJobs();
    }
  }, [event, mode]);

  const fetchJobTemplates = async () => {
    setJobTemplateLoading(true);
    setJobTemplateError('');
    try {
      console.log("tp:", trainingPlan);
      const instructorId = trainingPlan.instructor || trainingPlan.createdby || trainingPlan.createdBy;
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `/api/field-experience-events/jobs?instructor=${instructorId}&concentration=${trainingPlan.concentration_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJobTemplates(res.data.jobs);
    } catch (err) {
      setJobTemplateError('Failed to load job templates.');
    } finally {
      setJobTemplateLoading(false);
    }
  };

  const fetchJobsByEvent = async () => {
    setJobsByEventLoading(true);
    setJobsByEventError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `/api/field-experience-events/jobsbyevent?trainingplan=${trainingPlan.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJobsByEvent(res.data.jobsbyevent);
    } catch (err) {
      setJobsByEventError('Failed to load proposed jobs.');
    } finally {
      setJobsByEventLoading(false);
    }
  };

  // 2. In fetchJobsByEventForModal, set jobsByEventForModal instead of jobsByEvent
  const fetchJobsByEventForModal = async () => {
    setJobsByEventLoading(true);
    setJobsByEventError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `/api/field-experience-events/jobsbyevent?trainingplan=${selectedTrainingPlan.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJobsByEventForModal(res.data.jobsbyevent);
    } catch (err) {
      setJobsByEventError('Failed to load proposed jobs.');
    } finally {
      setJobsByEventLoading(false);
    }
  };

  // Fetch confirmed jobs for SAF approval validation
  const fetchConfirmedJobs = async () => {
    if (!event || !event.id) return;
    
    setConfirmedJobsLoading(true);
    setConfirmedJobsError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `/api/field-experience-events/jobsbyevent/by-event/${event.id}?status=event`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfirmedJobs(res.data.jobsbyevent);
    } catch (err) {
      setConfirmedJobsError('Failed to load confirmed jobs.');
    } finally {
      setConfirmedJobsLoading(false);
    }
  };

  const fetchTrainingPlansForEvent = async () => {
    setTrainingPlansLoading(true);
    setTrainingPlansError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `/api/field-experience-events/training-plans/by-event/${eventId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTrainingPlansList(res.data.trainingPlans);
    } catch (err) {
      setTrainingPlansError('Failed to load training plans.');
    } finally {
      setTrainingPlansLoading(false);
    }
  };

  const handleShowTrainingPlans = async () => {
    if (showTrainingPlansTable) {
      // Hide the training plans if they are already visible
      setShowTrainingPlansTable(false);
    }

    // Fetch training plans regardless of current visibility
    await fetchTrainingPlansForEvent();

    // Show the training plans
    setShowTrainingPlansTable(true);

    // Show Jobs icon in sidebar after first click
    if (!showJobsIcon) {
      setShowJobsIcon(true);
    }

    // Scroll to training plans section
    setTimeout(() => {
      const element = document.getElementById('training-plans-section');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };


  const handleTrainingPlanRowClick = (trainingPlan) => {
    setSelectedTrainingPlan(trainingPlan);
    setShowTrainingPlanModal(true);
  };

  const handleConfirmJob = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `/api/field-experience-events/jobsbyevent/${jobId}`,
        { status: 'event' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh the jobs list
      if (showTrainingPlanModal && selectedTrainingPlan) {
        await fetchJobsByEventForModal();
      } else if (selectedTrainingPlan) {
        const res = await axios.get(
          `/api/field-experience-events/jobsbyevent?trainingplan=${selectedTrainingPlan.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setJobsByEvent(res.data.jobsbyevent);
      }
      // Refresh confirmed jobs for SAF approval validation
      if (event && event.id && mode === 'coordinator') {
        await fetchConfirmedJobs();
      }
    } catch (err) {
      console.error('Failed to confirm job:', err);
    }
  };

  const handleUnconfirmJob = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `/api/field-experience-events/jobsbyevent/${jobId}`,
        { status: 'trainingPlan' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh the jobs list
      if (showTrainingPlanModal && selectedTrainingPlan) {
        await fetchJobsByEventForModal();
      } else if (selectedTrainingPlan) {
        const res = await axios.get(
          `/api/field-experience-events/jobsbyevent?trainingplan=${selectedTrainingPlan.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setJobsByEvent(res.data.jobsbyevent);
      }
      // Refresh confirmed jobs for SAF approval validation
      if (event && event.id && mode === 'coordinator') {
        await fetchConfirmedJobs();
      }
    } catch (err) {
      console.error('Failed to unconfirm job:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, item, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked
        ? [...prev[field], item]
        : prev[field].filter(i => i !== item)
    }));
  };

  const handleAddOfficialPartner = (partnerId) => {
    if (!formData.officialPartners.includes(partnerId)) {
      setFormData(prev => ({
        ...prev,
        officialPartners: [...prev.officialPartners, partnerId]
      }));
    }
  };

  const handleRemoveOfficialPartner = (partnerId) => {
    setFormData(prev => ({
      ...prev,
      officialPartners: prev.officialPartners.filter(id => id !== partnerId)
    }));
  };

  const handleCreateOfficialPartner = async () => {
    if (!newPartnerData.title.trim()) {
      setError('Partner title is required');
      return;
    }

    try {
      setCreatingPartner(true);
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/user-management/associations', {
        title: newPartnerData.title,
        displayname: newPartnerData.displayname || newPartnerData.title,
        type: 'Official Partner',
        school: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const newPartner = response.data.association;
      setAvailableOfficialPartners(prev => [...prev, newPartner]);
      handleAddOfficialPartner(newPartner.id);
      setShowCreatePartnerModal(false);
      setNewPartnerData({ title: '', displayname: '' });
    } catch (error) {
      console.error('Error creating official partner:', error);
      setError(error.response?.data?.error || 'Failed to create official partner');
    } finally {
      setCreatingPartner(false);
    }
  };

  const handleRegistrationLimitChange = (hasLimit) => {
    setFormData(prev => ({
      ...prev,
      hasRegistrationLimit: hasLimit,
      registrationLimit: hasLimit ? prev.registrationLimit : null
    }));
  };

  const handleCoverPhotoFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !eventId) return;
    setCoverPhotoUploading(true);
    setCoverPhotoUploadError('');
    try {
      // Generate a unique upload ID for this cover photo
      const uploadId = uuidv4();
      
      // Construct the S3 key to maintain folder organization
      const ext = file.name.split('.').pop();
      const key = `public/events/${eventId}/coverPhoto.${ext}`;
      
      // Get presigned URL for direct S3 upload
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/upload-url', {
        filename: file.name,
        contentType: file.type,
        fileSize: file.size,
        uploadId: uploadId,
        details: 'SAF form cover photo',
        key: key
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Upload file directly to S3 using presigned URL
      // Use fetch instead of axios to avoid CORS issues
      const uploadResponse = await fetch(response.data.presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });
      
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }
      
      // Update form with the S3 URL
      setFormData(prev => ({ ...prev, coverPhoto: response.data.uploadRecord.link }));
    } catch (err) {
      console.error('Cover photo upload error:', err);
      setCoverPhotoUploadError('Failed to upload cover photo.');
    } finally {
      setCoverPhotoUploading(false);
    }
  };

  const handleTrainingPlanFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if file is PDF
    if (file.type !== 'application/pdf') {
      setTrainingPlanError('Only PDF files are allowed.');
      return;
    }

    setTrainingPlanFile(file);
    setTrainingPlanError('');
  };

  const uploadTrainingPlanFile = async () => {
    if (!trainingPlanFile && trainingPlan.status === 'building') {
      setTrainingPlanError('Please select a PDF file to upload.');
      return;
    }

    try {
      setTrainingPlanUploading(true);
      
      // Generate a unique upload ID for this training plan
      const uploadId = uuidv4();
      
      // Construct the S3 key to maintain folder organization
      const key = `public/events/${eventId}/training-plans/${trainingPlanId}.pdf`;
      
      // Get presigned URL for direct S3 upload
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/upload-url', {
        filename: trainingPlanFile.name,
        contentType: trainingPlanFile.type,
        fileSize: trainingPlanFile.size,
        uploadId: uploadId,
        details: 'SAF form training plan',
        key: key
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Upload file directly to S3 using presigned URL
      const uploadResponse = await fetch(response.data.presignedUrl, {
        method: 'PUT',
        body: trainingPlanFile,
        headers: {
          'Content-Type': trainingPlanFile.type
        }
      });
      
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      return response.data.uploadRecord.link;
    } catch (error) {
      console.error('Error uploading training plan file:', error);
      setTrainingPlanError('Failed to upload training plan file.');
      throw error;
    } finally {
      setTrainingPlanUploading(false);
    }
  };

  const handleSubmitTrainingPlan = async () => {
    if (!trainingPlanFile && trainingPlan.status === 'building') {
      setTrainingPlanError('Please select a PDF file to upload.');
      return;
    }

    try {
      setActionOverlay(true);
      setTrainingPlanSubmitting(true);
      setTrainingPlanError('');
      const token = localStorage.getItem('token');

      console.log('Submitting training plan with data:', {
        status: trainingPlan.status,
        currentLink: trainingPlan.link,
        expectedUpdatedDate: trainingPlan.updateddate,
        expectedUpdatedDateType: typeof trainingPlan.updateddate,
        hasFile: !!trainingPlanFile
      });

      // First, check for version conflicts before uploading
      const conflictCheckData = {
        status: trainingPlan.status === 'building' ? 'submitted' : trainingPlan.status,
        link: trainingPlan.link, // Use current link for conflict check
        updateHistory: [
          ...(trainingPlan.updatehistory || []),
          {
            action: trainingPlan.status === 'building' ? 'submission' : 'resubmission',
            timestamp: new Date().toISOString()
          }
        ],
        expectedUpdatedDate: trainingPlan.updateddate
      };

      console.log('Sending conflict check data:', conflictCheckData);
      console.log('Expected updated date format:', typeof conflictCheckData.expectedUpdatedDate, conflictCheckData.expectedUpdatedDate);

      // Do a dry run to check for version conflicts
      const conflictCheckResponse = await axios.patch(`/api/field-experience-events/training-plans/${trainingPlanId}/check-conflict`, conflictCheckData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // If we get here, no conflict - proceed with file upload
      let fileUrl = null;
      if (trainingPlanFile) {
        fileUrl = await uploadTrainingPlanFile();
      }

      const updateData = {
        status: trainingPlan.status === 'building' ? 'submitted' : trainingPlan.status,
        link: fileUrl || trainingPlan.link,
        updateHistory: [
          ...(trainingPlan.updatehistory || []),
          {
            action: trainingPlan.status === 'building' ? 'submission' : 'resubmission',
            timestamp: new Date().toISOString()
          }
        ],
        expectedUpdatedDate: trainingPlan.updateddate
      };

      console.log('Sending update data:', updateData);

      const response = await axios.patch(`/api/field-experience-events/training-plans/${trainingPlanId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Training plan update response:', response.data);

      setTrainingPlan(response.data.trainingPlan);
      setTrainingPlanFile(null);

      // Clear the file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) {
        fileInput.value = '';
      }

    } catch (error) {
      console.error('Error submitting training plan:', error);

      if (error.response?.status === 409) {
        // Version conflict - show error and do NOT reload training plan data
        setTrainingPlanError('The training plan was updated while you were working. Upload was cancelled to prevent conflicts. <br/><b>Please reload the page to see the latest version.</b>');
        // Do NOT reload training plan data automatically
      } else {
        setTrainingPlanError('Failed to submit training plan.');
      }
    } finally {
      setTrainingPlanSubmitting(false);
      setActionOverlay(false);
    }
  };

  // Check if form has unsaved changes
  const hasUnsavedChanges = () => {
    if (!originalFormData) return false;
    
    // Helper to normalize arrays for comparison
    const normalizeArray = (arr) => {
      if (!Array.isArray(arr)) return [];
      return [...arr].sort().map(item => String(item));
    };
    
    // Helper to check if two arrays are equal (order independent)
    const arraysEqual = (a, b) => {
      const aNorm = normalizeArray(a);
      const bNorm = normalizeArray(b);
      if (aNorm.length !== bNorm.length) return false;
      return aNorm.every((val, idx) => val === bNorm[idx]);
    };
    
    // Check each field
    if (formData.title !== originalFormData.title) return true;
    if (formData.date !== originalFormData.date) return true;
    if (formData.leadInstructor !== originalFormData.leadInstructor) return true;
    if (formData.description !== originalFormData.description) return true;
    if (formData.location !== originalFormData.location) return true;
    if (formData.startTime !== originalFormData.startTime) return true;
    if (formData.endTime !== originalFormData.endTime) return true;
    if (formData.hoursAvailable !== originalFormData.hoursAvailable) return true;
    if (formData.type !== originalFormData.type) return true;
    if (formData.status !== originalFormData.status) return true;
    
    // Registration limit
    if (formData.hasRegistrationLimit !== originalFormData.hasRegistrationLimit) return true;
    if (formData.registrationLimit !== originalFormData.registrationLimit) return true;
    
    // Arrays - check if they're different
    if (!arraysEqual(formData.concentrations, originalFormData.concentrations)) return true;
    if (!arraysEqual(formData.subcalendarIds, originalFormData.subcalendarIds)) return true;
    if (!arraysEqual(formData.schools, originalFormData.schools)) return true;
    if (!arraysEqual(formData.territories, originalFormData.territories)) return true;
    if (!arraysEqual(formData.officialPartners, originalFormData.officialPartners || [])) return true;
    
    // Boolean flags
    if (formData.allowAllSchools !== originalFormData.allowAllSchools) return true;
    if (formData.allowAllTerritories !== originalFormData.allowAllTerritories) return true;
    
    // Cover photo
    if (formData.coverPhoto !== originalFormData.coverPhoto) return true;
    
    // TeamUp ID
    if (formData.teamUpId !== originalFormData.teamUpId) return true;
    
    return false;
  };

  const validateForm = () => {
    const errors = {};
    // Require at least one subcalendar
    const subcalIds = Array.isArray(formData.subcalendarIds)
      ? formData.subcalendarIds
      : (typeof formData.subcalendarIds === 'string' && formData.subcalendarIds.length > 0
        ? formData.subcalendarIds.split(',').map(id => id.trim())
        : []);
    if (!subcalIds || subcalIds.length === 0) {
      errors.subcalendarIds = 'At least one TeamUp subcalendar must be selected.';
    }
    // Schools/Territories validation mimicking backend
    if (!formData.allowAllSchools) {
      const selectedSchools = formData.schools || [];
      if (selectedSchools.length === 0) {
        if (formData.allowAllTerritories) {
          // Check if there are any schools in any territory
          const schoolsInAnyTerritory = availableSchools.filter(s => Array.isArray(s.territories) && s.territories.length > 0);
          if (schoolsInAnyTerritory.length === 0) {
            errors.schools = 'No schools found in any territories. Please select individual schools or ensure territories have schools assigned.';
          }
        } else if (formData.territories && formData.territories.length > 0) {
          // Check if there are any schools in the selected territories
          const schoolsInSelectedTerritories = availableSchools.filter(s =>
            Array.isArray(s.territories) && s.territories.some(tid => formData.territories.includes(tid.toString()))
          );
          if (schoolsInSelectedTerritories.length === 0) {
            errors.schools = 'Selected territories have no schools. Please select territories that contain schools or select individual schools.';
          }
        } else if (!formData.allowAllTerritories && (!formData.schools || formData.schools.length === 0) && (!formData.territories || formData.territories.length === 0)) {
          errors.schools = 'Select at least one school or territory, or enable "All Schools" or "All Territories".';
          errors.territories = 'Select at least one school or territory, or enable "All Schools" or "All Territories".';
        }
      }
      // If schools are selected, no need to check territories
    }
    return errors;
  };

  const computeCanEditSaf = () => {
    if (!event || !userData) return false;
    if (mode === 'trainingplan') return true;
    return safAccess.canEdit;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!computeCanEditSaf()) return;
    setSaving(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSaving(false);
      return;
    }

    // Wait for any pending cover photo upload to complete
    if (coverPhotoUploading) {
      setError('Please wait for the cover photo upload to complete before submitting.');
      setSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');

      // Prepare the data for submission
      const submissionData = {
        ...formData,
        registrationLimit: formData.hasRegistrationLimit ? formData.registrationLimit : null,
        subcalendarIds: Array.isArray(formData.subcalendarIds)
          ? formData.subcalendarIds
          : (typeof formData.subcalendarIds === 'string' && formData.subcalendarIds.length > 0
            ? formData.subcalendarIds.split(',').map(id => id.trim())
            : []),
        // Set schools and territories to empty arrays if allowAll is true or no selections
        schools: formData.allowAllSchools || !formData.schools || formData.schools.length === 0 ? [] : formData.schools,
        territories: formData.allowAllTerritories || !formData.territories || formData.territories.length === 0 ? [] : formData.territories,
        officialPartners: formData.officialPartners || []
      };
      // Only include status if in official stage
      if (event.stage === 'official') {
        submissionData.status = formData.status || 'live';
      }

      // For version check: send expectedEventUpdatedDate if in saf/official stage
      if (event.stage === 'saf' || event.stage === 'official') {
        submissionData.expectedEventUpdatedDate = event.updateddate;
      }

      await axios.patch(`/api/field-experience-events/${eventId}/saf`, submissionData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update original form data after successful save
      setOriginalFormData(JSON.parse(JSON.stringify(formData)));

      setSuccess('SAF form submitted successfully!');
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Error submitting SAF form:', error);
      setError(error.response?.data?.error || 'Failed to submit SAF form');
    } finally {
      setSaving(false);
    }
  };

  if (!userData) {
    return <div style={styles.container}>Please log in to access the SAF form.</div>;
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!event) {
    return <div style={styles.container}>Event not found.</div>;
  }

  // Check if user can edit this event
  const canEdit = computeCanEditSaf();

  // Show critical error overlay if there's a critical error OR user is not authorized
  // Access gate: server saf-access response (not URL mode or client capability flags)
  const hasSafAccess = mode === 'trainingplan' || safAccess.canView;

  if (criticalError || !hasSafAccess) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255,255,255,0.95)',
        zIndex: 11002,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 12,
          padding: 32,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          textAlign: 'center',
          maxWidth: 500,
          border: '1px solid #e3e8f0'
        }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#d32f2f', marginBottom: 16 }}>
            ⚠️ Access Restricted
          </div>
          <div style={{ fontSize: 16, color: '#666', marginBottom: 24, lineHeight: 1.5 }}>
            {criticalError || 'You are not authorized to access or edit this SAF form. If you believe this is an error, please contact your administrator.'}
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={() => navigate('/my-events')}
              style={{
                background: '#d32f2f',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '12px 24px',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Go to My Events
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (saving) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255,255,255,0.8)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.success}>{success}</div>
      </div>
    );
  }

  // Check if force approve is available for the current reviewer
  const canForceApprove = () => {
    if (!reviewer || !event) return false;
    return reviewer.safdecision === 'approved' && event.status === 'pending';
  };

  const safFormReadOnly = !canEdit && mode !== 'trainingplan';

  // Build sidebar items dynamically, adding Jobs after first click
  const sidebarItems = [
    { id: 'section-event-details', icon: '📝', label: 'Basic Info' },
    { id: 'section-schedule', icon: '🕒', label: 'Schedule' },
    { id: 'section-controls', icon: '⚙️', label: 'Controls' },
    { id: 'section-concentrations', icon: '🧑‍🏫', label: 'Concentrations' },
    { id: 'section-teamup', icon: '📅', label: 'Calendars' },
    { id: 'section-audience', icon: '🏫', label: 'Audience' },
    { id: 'section-media', icon: '🖼️', label: 'Media' },
    { id: 'section-official-partners', icon: '🤝', label: 'Partners' }
  ];
  if (mode === 'trainingplan') {
    sidebarItems.push({ id: 'training-plan-section', icon: '📄', label: 'My Training Plan' });
  }
  if (showJobsIcon) {
    sidebarItems.push({ id: 'training-plans-section', icon: '💼', label: 'Jobs' });
  }

  return (
    <div className="saf-form" style={styles.container}>
      {/* Thin sticky sidebar for section navigation */}
      <div style={styles.sidebar} aria-label="Section Navigation">
        {sidebarItems.map(s => (
          <button
            key={s.id}
            title={s.label}
            style={styles.sidebarButton}
            className="sidebar-item"
            onClick={() => scrollToSection(s.id)}
          >
            {s.icon}
            <span className="sidebar-label">{s.label}</span>
          </button>
        ))}
      </div>
      <h1 style={styles.title}>
        Schedule Approval Form (SAF)
      </h1>

      <style>{`
        .saf-form input:not([type="checkbox"]):not([type="radio"]):not([type="file"]),
        .saf-form select,
        .saf-form textarea {
          border: 1px solid #e2e8f0;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
          font-size: 0.78rem;
          color: #334155;
          background-color: #fafbfc;
          box-shadow: none;
          font-weight: 500;
          padding: 0.32rem 0.5rem;
          border-radius: 5px;
          min-height: 28px;
        }
        .saf-form textarea {
          min-height: 56px;
          line-height: 1.4;
        }
        .saf-form input:not([type="checkbox"]):not([type="radio"]):not([type="file"]):hover,
        .saf-form select:hover,
        .saf-form textarea:hover {
          border-color: #cbd5e1;
          background-color: #fff;
        }
        .saf-form input:not([type="checkbox"]):not([type="radio"]):not([type="file"]):focus,
        .saf-form select:focus,
        .saf-form textarea:focus {
          border-color: #94a3b8;
          box-shadow: 0 0 0 2px rgba(148, 163, 184, 0.2);
          outline: none;
          background-color: #fff;
        }
        .saf-form input[type="checkbox"],
        .saf-form input[type="radio"] {
          width: 14px;
          height: 14px;
          min-height: 0;
          min-width: 14px;
          margin: 0;
          padding: 0;
          border: 1px solid #cbd5e1;
          border-radius: 3px;
          background-color: #fff;
          accent-color: #475569;
          cursor: pointer;
          flex-shrink: 0;
          box-shadow: none;
          transition: border-color 0.12s ease;
        }
        .saf-form input[type="radio"] {
          border-radius: 50%;
        }
        .saf-form input[type="checkbox"]:hover,
        .saf-form input[type="radio"]:hover {
          border-color: #94a3b8;
        }
        .saf-form input[type="checkbox"]:focus,
        .saf-form input[type="radio"]:focus {
          outline: none;
          border-color: #64748b;
          box-shadow: 0 0 0 2px rgba(148, 163, 184, 0.22);
        }
        .saf-form input[type="checkbox"]:disabled,
        .saf-form input[type="radio"]:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .saf-form ::placeholder { color: #94a3b8; }
        .saf-form label {
          font-size: 0.68rem;
          color: #64748b;
          font-weight: 600;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }
        .saf-form .saf-checkbox-label,
        .saf-form .saf-radio-label,
        .saf-form label:has(input[type="checkbox"]),
        .saf-form label:has(input[type="radio"]) {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.75rem;
          font-weight: 500;
          color: #475569;
          text-transform: none;
          letter-spacing: normal;
          line-height: 1.3;
          cursor: pointer;
          padding: 1px 0;
          margin: 0;
        }
        .saf-form .saf-checkbox-group {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .saf-form .saf-checkbox-group--nested {
          margin-left: 0.75rem;
          margin-top: 2px;
          padding-left: 8px;
          border-left: 1px solid #e2e8f0;
        }
        .saf-form input[type="file"] {
          font-size: 0.75rem;
          color: #475569;
          padding: 0.15rem 0;
          border: none;
          background: transparent;
          min-height: 0;
          box-shadow: none;
        }
        .saf-form .card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 10px 12px;
          box-shadow: none;
          transition: border-color 0.2s ease;
        }
        .saf-form .saf-section + .saf-section { margin-top: 10px; }
        .saf-form .saf-section-title {
          font-size: 0.82rem;
          font-weight: 600;
          color: #475569;
          margin: 0 0 8px 0;
          letter-spacing: 0.01em;
        }
        .saf-form .row { display: grid; gap: 8px; }
        .saf-form .row.two { grid-template-columns: 1fr 1fr; }
        .saf-form .row.three { grid-template-columns: 1fr 1fr 1fr; }
        @media (max-width: 900px) {
          .saf-form .row.two { grid-template-columns: 1fr; }
          .saf-form .row.three { grid-template-columns: 1fr; }
        }
        .saf-form .highlight-flash {
          border-color: #93c5fd !important;
          box-shadow: 0 0 0 1px rgba(147, 197, 253, 0.35);
        }
        .saf-form .sidebar-item {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: visible;
        }
        .saf-form .sidebar-item .sidebar-label {
          position: absolute;
          left: 30px;
          top: 50%;
          transform: translateY(-50%) translateX(-6px);
          opacity: 0;
          white-space: nowrap;
          color: #475569;
          background: #fff;
          border: 1px solid #e2e8f0;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
          border-radius: 5px;
          padding: 3px 7px;
          font-weight: 600;
          font-size: 0.68rem;
          pointer-events: none;
          transition: transform 0.2s ease, opacity 0.2s ease;
        }
        .saf-form .sidebar-item:hover .sidebar-label,
        .saf-form .sidebar-item:active .sidebar-label {
          opacity: 1;
          transform: translateY(-50%) translateX(0);
        }
        .saf-form h1 {
          letter-spacing: -0.02em;
        }
        .saf-form button {
          font-size: 0.78rem;
        }
        /* Notification bubble animation */
        @keyframes fadeInOut {
          0% {
            opacity: 0;
            transform: translateY(-10px);
          }
          15% {
            opacity: 1;
            transform: translateY(0);
          }
          85% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-10px);
          }
        }
        /* Save SAF button entrance animation */
        @keyframes slideUpBounce {
          0% {
            opacity: 0;
            transform: translateY(30px) scale(0.9);
          }
          60% {
            opacity: 1;
            transform: translateY(-8px) scale(1.05);
          }
          80% {
            transform: translateY(2px) scale(1);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .save-saf-button-entering {
          animation: slideUpBounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        /* Compact training plan row hover */
        .saf-form .compact-training-plan-row:hover {
          background-color: #f8f9fa !important;
          border-color: #d0d7e2 !important;
        }
        .saf-form .compact-view-button:hover {
          background-color: #007bff !important;
          color: white !important;
        }
        /* Job list item hover effects */
        .saf-form .job-item-content:hover {
          border-color: #cbd5e1 !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04) !important;
        }
        .saf-form .propose-button:hover {
          background-color: #007bff !important;
          color: white !important;
        }
        .saf-form .create-button:hover {
          background-color: #f8fafc !important;
          border-color: #cbd5e1 !important;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12) !important;
          color: #334155 !important;
        }
        .saf-form .icon-button:hover {
          opacity: 0.7;
        }
        .saf-form .create-button-icon:hover {
          background-color: #f8fafc !important;
          border-color: #cbd5e1 !important;
          transform: scale(1.05);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
        }
      `}</style>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}
      {safFormReadOnly && (
        <div style={styles.readOnlyBanner}>
          View-only access — you can review this SAF but cannot make changes.
        </div>
      )}

      <form id="saf-form" onSubmit={handleSubmit} style={styles.form}>
        <fieldset disabled={safFormReadOnly} style={styles.readOnlyFieldset}>
        {/* Event Details */}
        <div id="section-event-details" className="saf-section card">
          <h3 className="saf-section-title">Basic Info</h3>
          {/* Full-width Title */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => handleInputChange('title', e.target.value)}
              style={styles.input}
              required
              disabled={saving || mode === 'trainingplan'}
              placeholder="Enter event title"
            />
          </div>
          {/* Date and Lead Instructor on one row */}
          <div className="row two" style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Date</label>
              <input
                type="date"
                value={formData.date}
                onChange={e => handleInputChange('date', e.target.value)}
                style={styles.input}
                required
                disabled={saving || mode === 'trainingplan'}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Lead Instructor</label>
              <select
                value={formData.leadInstructor}
                onChange={(e) => handleInputChange('leadInstructor', e.target.value)}
                style={styles.select}
                required
                disabled={saving || mode === 'leadinstructor' || mode === 'trainingplan'} // Disable for lead instructor mode and trainingplan mode
              >
                <option value="">Select Lead Instructor</option>
                {availableInstructors.map(instructor => (
                  <option key={instructor.account_id} value={instructor.account_id}>
                    {instructor.first_name} {instructor.last_name}
                  </option>
                ))}
              </select>
              {mode === 'leadinstructor' && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Lead instructors cannot change the lead instructor assignment
                </div>
              )}
              {mode === 'trainingplan' && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Training plan view - all fields are read-only
                </div>
              )}
            </div>
          </div>
          {/* Description full width */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              ref={descriptionRef}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              onInput={(e) => autoResizeTextarea(e.target)}
              style={{ ...styles.textarea, overflow: 'hidden', resize: 'none' }}
              rows="3"
              required
              disabled={saving || mode === 'trainingplan'}
              placeholder="Describe the field experience event..."
            />
          </div>
        </div>
        {/* Schedule */}
        <div id="section-schedule" className="saf-section card">
          <h3 className="saf-section-title">Schedule</h3>
          {/* Full-width Location */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={e => handleInputChange('location', e.target.value)}
              style={styles.input}
              required
              disabled={saving || mode === 'trainingplan'}
              placeholder="Enter event location"
            />
          </div>
          {/* Start, End, Hours on one row */}
          <div className="row three" style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Start Time</label>
              <input
                type="time"
                value={formData.startTime}
                onChange={e => handleInputChange('startTime', e.target.value)}
                style={styles.input}
                required
                disabled={saving || mode === 'trainingplan'}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>End Time</label>
              <input
                type="time"
                value={formData.endTime}
                onChange={e => handleInputChange('endTime', e.target.value)}
                style={styles.input}
                required
                disabled={saving || mode === 'trainingplan'}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Hours Available</label>
              <input
                type="text"
                value={formData.hoursAvailable}
                onChange={e => handleInputChange('hoursAvailable', e.target.value)}
                style={styles.input}
                required
                disabled={saving || mode === 'trainingplan'}
                placeholder="e.g., 8 hours"
              />
            </div>
          </div>
        </div>
        {/* Controls */}
        <div id="section-controls" className="saf-section card">
          <h3 className="saf-section-title">Controls</h3>
          {/* Status and Type on one row */}
          <div className="row two" style={styles.formRow}>
            {event.stage === 'official' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <select
                  value={formData.status || event.status || 'live'}
                  onChange={e => handleInputChange('status', e.target.value)}
                  style={styles.select}
                  disabled={saving || mode === 'trainingplan'}
                >
                  <option value="live">Live</option>
                  <option value="locked">Locked</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>
            )}
            <div style={styles.formGroup}>
              <label style={styles.label}>Type</label>
              <select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                style={styles.select}
                required
                disabled={saving || mode === 'trainingplan'}
              >
                <option value="In-Person">In-Person</option>
                <option value="Virtual">Virtual</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </div>
          </div>
          {/* Registration Limit moved here */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Registration Limit</label>
            <div style={styles.registrationLimitGroup}>
              <label className="saf-checkbox-label" style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.hasRegistrationLimit}
                  onChange={e => handleRegistrationLimitChange(e.target.checked)}
                  disabled={saving || mode === 'trainingplan'}
                />
                Has Registration Limit
              </label>
              {formData.hasRegistrationLimit && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.9rem', color: '#666' }}>Limit:</span>
                  <input
                    type="number"
                    value={formData.registrationLimit || ''}
                    onChange={e => handleInputChange('registrationLimit', parseInt(e.target.value) || null)}
                    style={styles.input}
                    min="1"
                    disabled={saving || mode === 'trainingplan'}
                    placeholder="Enter limit"
                  />
                </div>
              )}
            </div>
            {mode === 'trainingplan' && !formData.hasRegistrationLimit && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                No registration limit set
              </div>
            )}
          </div>
        </div>

        
        {/* Concentrations */}
        <div id="section-concentrations" className="saf-section card">
          <h3 className="saf-section-title">Concentrations</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={styles.label}>Concentrations</label>
          {trainingPlansList.filter(tp => tp.status === 'submitted').length > 0 && (
            <button
              type="button"
              onClick={handleShowTrainingPlans}
              style={{
                ...styles.button,
                padding: '4px 12px',
                fontSize: '12px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              JOBS
            </button>
          )}
          </div>
          <div className="saf-checkbox-group" style={styles.checkboxGroup}>
          {availableConcentrations.length > 0 ? (
            availableConcentrations.map(concentration => (
              <label key={concentration.id} className="saf-checkbox-label" style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.concentrations.includes(concentration.id)}
                  onChange={e => handleArrayChange('concentrations', concentration.id, e.target.checked)}
                  disabled={saving || mode === 'trainingplan'}
                />
                {concentration.title}
              </label>
            ))
          ) : (
            <div style={{ fontSize: '12px', color: '#666' }}>No concentrations available</div>
          )}
          </div>
          {mode === 'trainingplan' && formData.concentrations.length === 0 && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              No concentrations selected
            </div>
          )}
        </div>
        {/* TeamUp Calendars */}
        <div id="section-teamup" className="saf-section card">
          <h3 className="saf-section-title">TeamUp Calendars</h3>
          <div style={styles.formGroup}>
          <label style={styles.label}>TeamUp Subcalendars</label>
          <div className="saf-checkbox-group" style={styles.checkboxGroup}>
            {availableSubcalendars.map(subcal => (
              <label key={subcal.id} className="saf-checkbox-label" style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.subcalendarIds.includes(subcal.id.toString())}
                  onChange={e => handleArrayChange('subcalendarIds', subcal.id.toString(), e.target.checked)}
                  disabled={saving || mode === 'trainingplan'}
                />
                {subcal.name}
              </label>
            ))}
          </div>
          {formErrors.subcalendarIds && <div style={styles.error}>{formErrors.subcalendarIds}</div>}
          </div>
        </div>
        {/* Eligible Audience */}
        <div id="section-audience" className="saf-section card">
          <h3 className="saf-section-title">Eligible Audience</h3>
          <div className="row two" style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Schools</label>
            <div className="saf-checkbox-group" style={styles.checkboxGroup}>
              <label className="saf-checkbox-label" style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.allowAllSchools}
                  onChange={e => handleInputChange('allowAllSchools', e.target.checked)}
                  disabled={saving || mode === 'trainingplan'}
                />
                All Schools
              </label>
              {!formData.allowAllSchools && availableSchools.map(school => (
                <label key={school.school_id} className="saf-checkbox-label" style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.schools.includes(school.school_id.toString())}
                    onChange={e => handleArrayChange('schools', school.school_id.toString(), e.target.checked)}
                    disabled={saving || mode === 'trainingplan'}
                  />
                  {school.school_name}
                </label>
              ))}
            </div>
            {formErrors.schools && <div style={styles.error}>{formErrors.schools}</div>}
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Territories</label>
            <div className="saf-checkbox-group" style={styles.checkboxGroup}>
              <label className="saf-checkbox-label" style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.allowAllTerritories}
                  onChange={e => handleInputChange('allowAllTerritories', e.target.checked)}
                  disabled={saving || mode === 'trainingplan'}
                />
                All Territories
              </label>
              {!formData.allowAllTerritories && availableTerritories.map(territory => {
                const schoolsInTerritory = availableSchools.filter(school =>
                  Array.isArray(school.territories) && school.territories.includes(territory.territory_id)
                );
                return (
                  <div key={territory.territory_id} style={styles.territoryItem}>
                    <label className="saf-checkbox-label" style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={formData.territories.includes(territory.territory_id.toString())}
                        onChange={e => handleArrayChange('territories', territory.territory_id.toString(), e.target.checked)}
                        disabled={saving || mode === 'trainingplan'}
                      />
                      <strong>{territory.title}</strong>
                    </label>
                    {schoolsInTerritory.length > 0 && (
                      <div style={styles.territorySchools}>
                        <em>Schools in this territory:</em>
                        <ul style={styles.schoolList}>
                          {schoolsInTerritory.map(school => (
                            <li key={school.school_id} style={styles.schoolListItem}>
                              {school.school_name}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {formErrors.territories && <div style={styles.error}>{formErrors.territories}</div>}
          </div>
          </div>
        </div>
        {/* Media */}
        <div id="section-media" className="saf-section card">
          <h3 className="saf-section-title">Media</h3>
          <div style={styles.formGroup}>
          <label style={styles.label}>Cover Photo</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleCoverPhotoFileChange}
            disabled={saving || coverPhotoUploading || mode === 'trainingplan'}
          />
          {coverPhotoUploading && <div style={{ color: '#888', marginTop: 4 }}>Uploading...</div>}
          {coverPhotoUploadError && <div style={{ color: '#c00', marginTop: 4 }}>{coverPhotoUploadError}</div>}
          {formData.coverPhoto && (
            <div style={{ marginTop: 8 }}>
              <img src={formData.coverPhoto} alt="Cover" style={{ maxWidth: 300, maxHeight: 200, borderRadius: 8 }} />
              <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>{formData.coverPhoto}</div>
            </div>
          )}
          </div>
        </div>

        {/* Official Partners */}
        <div id="section-official-partners" className="saf-section card">
          <h3 className="saf-section-title">Official Partner(s)</h3>
          <div style={styles.formGroup}>
            <div style={styles.partnersContainer}>
              {formData.officialPartners.map(partnerId => {
                const partner = availableOfficialPartners.find(p => p.id === partnerId);
                if (!partner) return null;
                return (
                  <div key={partnerId} style={styles.partnerItem}>
                    <span>{partner.title || partner.displayname}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveOfficialPartner(partnerId)}
                      style={styles.removeButton}
                      disabled={saving || mode === 'trainingplan'}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
              <div style={styles.partnerActions}>
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      handleAddOfficialPartner(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  style={styles.partnerSelect}
                  disabled={saving || mode === 'trainingplan'}
                >
                  <option value="">Select existing partner...</option>
                  {availableOfficialPartners
                    .filter(p => !formData.officialPartners.includes(p.id))
                    .map(partner => (
                      <option key={partner.id} value={partner.id}>
                        {partner.title || partner.displayname}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowCreatePartnerModal(true)}
                  style={styles.addPartnerButton}
                  disabled={saving || mode === 'trainingplan'}
                >
                  + Create New Partner
                </button>
              </div>
            </div>
          </div>
        </div>

        

        

        </fieldset>
      </form>

      {/* Training Plan Section */}
      {mode === 'trainingplan' && (
        <div id="training-plan-section" style={styles.trainingPlanSection}>

          {trainingPlanLoading && (
            <div style={styles.trainingPlanLoading}>
              <LoadingSpinner />
              <p>Loading training plan...</p>
            </div>
          )}

          {trainingPlanError && (
            <div style={trainingPlanError.includes('version') || trainingPlanError.includes('conflict') ? styles.versionConflictError : styles.error}
              dangerouslySetInnerHTML={{ __html: trainingPlanError }} />
          )}

          {trainingPlan && !trainingPlanLoading && !trainingPlanError && (
            <div style={styles.trainingPlanContent}>
              <div style={styles.trainingPlanHeader}>
                <h2 style={styles.trainingPlanTitle}>
                  Training Plan - {trainingPlan.concentration_title || trainingPlan.concentration?.title || 'Unknown Concentration'}
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {trainingPlan.status === 'building' && (
                    <span style={{ ...styles.statusPill, ...styles.statusPillBuilding }}>Building</span>
                  )}
                  {trainingPlan.status === 'submitted' && (
                    <span style={{ ...styles.statusPill, ...styles.statusPillSubmitted }}>Submitted</span>
                  )}
                  {trainingPlan.revision > 0 && (
                    <span style={styles.revisionMeta}>
                      Rev {trainingPlan.revision}
                    </span>
                  )}
                </div>
              </div>

              <div style={styles.trainingPlanSubtext}>
                A file upload and at least 1 job proposal is required to submit a training plan.
              </div>

              {trainingPlan.status !== 'building' && trainingPlan.status !== 'submitted' && (
                <div style={styles.trainingPlanStatus}>
                  <strong>Status:</strong> {trainingPlan.status}
                  {trainingPlan.revision > 0 && (
                    <span style={{ marginLeft: '1rem', color: '#666' }}>
                      (Revision {trainingPlan.revision})
                    </span>
                  )}
                </div>
              )}

              {trainingPlan.status === 'building' && (
                <div style={styles.trainingPlanUpload}>
                  <div style={styles.uploaderRow}>
                    <input
                      id={`tp-upload-${trainingPlan.id}`}
                      type="file"
                      accept=".pdf"
                      onChange={handleTrainingPlanFileChange}
                      style={{ display: 'none' }}
                      disabled={trainingPlanUploading}
                      key={`building-${trainingPlan.id}`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const el = document.getElementById(`tp-upload-${trainingPlan.id}`);
                        if (el) el.click();
                      }}
                      disabled={trainingPlanUploading}
                      style={{
                        ...styles.uploadButton,
                        ...(trainingPlanUploading ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                      }}
                      title="Choose PDF file"
                    >
                      Choose PDF
                    </button>
                    <span style={styles.fileName}>
                      {trainingPlanFile ? trainingPlanFile.name : 'No file selected'}
                    </span>
                    <button
                      type="button"
                      onClick={handleSubmitTrainingPlan}
                      disabled={!trainingPlanFile || trainingPlanUploading || trainingPlanSubmitting || jobsByEvent.length === 0}
                      style={{
                        ...styles.button,
                        ...styles.submitButton,
                        ...styles.uploaderSubmitButton,
                        ...((!trainingPlanFile || trainingPlanUploading || trainingPlanSubmitting || jobsByEvent.length === 0) ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                      }}
                      title={jobsByEvent.length === 0 ? 'Propose at least one job before submitting' : 'Submit training plan'}
                    >
                      {trainingPlanSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </div>
              )}

              {trainingPlan.status === 'submitted' && (
                <div style={styles.trainingPlanSubmitted}>
                  <div style={styles.trainingPlanSubmittedRow}>
                    {trainingPlan.link && (
                      <a
                        href={trainingPlan.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ ...styles.pdfLinkTile, ...styles.submittedTile }}
                        title="View submitted PDF"
                      >
                        <div style={styles.pdfIcon}>📄</div>
                        <div style={styles.pdfCaption}>View submitted PDF</div>
                      </a>
                    )}

                    <div style={{ ...styles.trainingPlanReplace, ...styles.submittedReplace }}>
                    <div style={styles.uploaderRow}>
                      <input
                        id={`tp-replace-${trainingPlan.id}`}
                        type="file"
                        accept=".pdf"
                        onChange={handleTrainingPlanFileChange}
                        style={{ display: 'none' }}
                        disabled={trainingPlanUploading}
                        key={`submitted-${trainingPlan.id}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const el = document.getElementById(`tp-replace-${trainingPlan.id}`);
                          if (el) el.click();
                        }}
                        disabled={trainingPlanUploading}
                        style={{
                          ...styles.uploadButton,
                          ...(trainingPlanUploading ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                        }}
                        title="Choose PDF file"
                      >
                        Choose PDF
                      </button>
                      <span style={styles.fileName}>
                        {trainingPlanFile ? trainingPlanFile.name : 'No file selected'}
                      </span>
                      <button
                        type="button"
                        onClick={handleSubmitTrainingPlan}
                        disabled={!trainingPlanFile || trainingPlanUploading || trainingPlanSubmitting || jobsByEvent.length === 0}
                        style={{
                          ...styles.button,
                          ...styles.submitButton,
                          ...styles.uploaderSubmitButton,
                          ...((!trainingPlanFile || trainingPlanUploading || trainingPlanSubmitting || jobsByEvent.length === 0) ? { opacity: 0.6, cursor: 'not-allowed' } : {})
                        }}
                        title={jobsByEvent.length === 0 ? 'Confirm at least one job before replacing' : 'Replace training plan'}
                      >
                        {trainingPlanSubmitting ? 'Replacing...' : 'Replace'}
                      </button>
                    </div>
                    {trainingPlan.revision > 0 && (
                      <div style={styles.replaceHint}>This will create revision {trainingPlan.revision + 1}</div>
                    )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Jobs Two-Column Layout */}
          <div style={styles.jobsTwoColContainer}>
            {/* Left Column: Job Templates */}
            <div style={styles.jobsColumn}>
              <div style={styles.columnHeaderWithAction}>
                <h3 style={{ ...styles.columnHeader, marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>Job Templates for This Concentration</h3>
                <button
                  className="create-button-icon"
                  style={styles.createButtonIcon}
                  onClick={() => setShowJobTemplateForm(true)}
                  title="Create New Job Template"
                >
                  +
                </button>
              </div>
            {jobTemplateLoading ? (
              <div>Loading job templates...</div>
            ) : jobTemplateError ? (
              <div style={styles.error}>{jobTemplateError}</div>
            ) : jobTemplates.length === 0 ? (
              <div>
                <p style={styles.emptyStateText}>No job templates found for this concentration.</p>
              </div>
            ) : (
              <div>
                <ol style={styles.jobList}>
                  {jobTemplates.map(job => {
                    // Map other concentration IDs to titles
                    const otherConcentrationTitles = Array.isArray(job.otherconcentrations) && job.otherconcentrations.length > 0
                      ? job.otherconcentrations
                        .map(concId => availableConcentrations.find(c => c.id === concId)?.title)
                        .filter(title => title) // Remove any undefined titles
                      : [];

                    return (
                      <li key={job.id} style={styles.jobListItem}>
                        <div className="job-item-content" style={styles.jobItemContent}>
                          <div style={styles.jobItemHeader}>
                            <span style={styles.jobTitle}>{job.title}</span>
                            <button
                              className="propose-button"
                              style={styles.proposeButton}
                              onClick={() => {
                                const hasOtherConcs = Array.isArray(job.otherconcentrations) && job.otherconcentrations.length > 0;
                                setProposeJobModal({
                                  job,
                                  slots: 1,
                                  title: job.title,
                                  description: job.description,
                                  otherConcentrationsEnabled: hasOtherConcs,
                                  otherConcentrations: hasOtherConcs ? job.otherconcentrations : []
                                });
                              }}
                              title="Propose for Event"
                            >
                              Propose
                            </button>
                          </div>
                          <div style={styles.jobDescription}>{job.description}</div>
                          {otherConcentrationTitles.length > 0 && (
                            <div style={styles.jobMeta}>
                              <span style={styles.jobMetaLabel}>Other concentrations:</span> {otherConcentrationTitles.join(', ')}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
            </div>

            {/* Right Column: Proposed Jobs */}
            <div style={styles.proposedColumn}>
              <h3 style={styles.columnHeader}>Proposed Jobs for This Training Plan</h3>
              {jobsByEventLoading ? (
                <div>Loading proposed jobs...</div>
              ) : jobsByEventError ? (
                <div style={styles.error}>{jobsByEventError}</div>
              ) : jobsByEvent.length === 0 ? (
                <div style={styles.emptyStateText}>No jobs proposed yet. Propose a job on the left.</div>
              ) : (
                <ol style={styles.jobList}>
                  {jobsByEvent.map(jbe => {
                    // Map other concentration IDs to titles
                    const otherConcentrationTitles = Array.isArray(jbe.otherconcentrations) && jbe.otherconcentrations.length > 0
                      ? jbe.otherconcentrations
                        .map(concId => availableConcentrations.find(c => c.id === concId)?.title)
                        .filter(title => title) // Remove any undefined titles
                      : [];

                    return (
                      <li key={jbe.id} style={styles.jobListItem}>
                        <div className="job-item-content" style={styles.jobItemContent}>
                          <div style={styles.jobItemHeader}>
                            <div style={styles.jobItemTitleRow}>
                              <span style={styles.jobTitle}>{jbe.title}</span>
                              {jbe.status === 'event' && mode !== 'coordinator' && (
                                <span style={styles.confirmedBadge}>✅ Confirmed</span>
                              )}
                            </div>
                            <div style={styles.jobItemActions}>
                              {mode === 'trainingplan' && jbe.status === 'trainingPlan' && (
                                <>
                                  <button
                                    className="icon-button"
                                    style={styles.iconButton}
                                    onClick={() => setEditJobByEvent({
                                      ...jbe,
                                      otherConcentrationsEnabled: Array.isArray(jbe.otherconcentrations) && jbe.otherconcentrations.length > 0,
                                      otherConcentrations: Array.isArray(jbe.otherconcentrations) ? jbe.otherconcentrations : []
                                    })}
                                    title="Edit job"
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    className="icon-button"
                                    style={styles.iconButton}
                                    onClick={async () => {
                                      if (!window.confirm('Are you sure you want to delete this job?')) return;
                                      try {
                                        await axios.delete(`/api/field-experience-events/jobsbyevent/${jbe.id}`, {
                                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                        });
                                        fetchJobsByEvent();
                                      } catch (err) {
                                        alert('Failed to delete job.');
                                      }
                                    }}
                                    title="Delete job"
                                  >
                                    ❌
                                  </button>
                                </>
                              )}
                              {jbe.status === 'event' && mode === 'coordinator' && (
                                <button
                                  style={styles.actionButton}
                                  onClick={() => handleUnconfirmJob(jbe.id)}
                                >
                                  Unconfirm
                                </button>
                              )}
                              {jbe.status === 'trainingPlan' && mode === 'coordinator' && (
                                <button
                                  style={{ ...styles.actionButton, ...styles.confirmButton }}
                                  onClick={() => handleConfirmJob(jbe.id)}
                                >
                                  Confirm
                                </button>
                              )}
                            </div>
                          </div>
                          <div style={styles.jobDescription}>{jbe.description}</div>
                          <div style={styles.jobMeta}>
                            <span style={styles.jobMetaItem}><strong>Slots:</strong> {jbe.slots}</span>
                            {otherConcentrationTitles.length > 0 && (
                              <span style={styles.jobMetaItem}>
                                <strong>Other concentrations:</strong> {otherConcentrationTitles.join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </div>

          {/* Propose Job Modal */}
      {proposeJobModal && (
        <div style={{ background: 'rgba(0,0,0,0.3)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 11000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: 'white', padding: 24, borderRadius: 8, minWidth: 320, maxHeight: '80vh', overflowY: 'auto' }}>
                <h4>Propose Job for Event</h4>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="propose-job-title">Job Title</label>
                  <input
                    id="propose-job-title"
                    type="text"
                    value={proposeJobModal.title}
                    onChange={e => setProposeJobModal({ ...proposeJobModal, title: e.target.value })}
                    style={{ ...styles.input, opacity: proposeJobLoading ? 0.6 : 1 }}
                    placeholder="Job Title"
                    disabled={proposeJobLoading}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="propose-job-description">Job Description</label>
                  <textarea
                    id="propose-job-description"
                    value={proposeJobModal.description}
                    onChange={e => setProposeJobModal({ ...proposeJobModal, description: e.target.value })}
                    style={{ ...styles.textarea, opacity: proposeJobLoading ? 0.6 : 1 }}
                    placeholder="Job Description"
                    disabled={proposeJobLoading}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="propose-job-slots">Slots (positions for this event)</label>
                  <input
                    id="propose-job-slots"
                    type="number"
                    value={typeof proposeJobModal.slots === 'number' ? proposeJobModal.slots : (proposeJobModal.slots ? parseInt(proposeJobModal.slots) : 1)}
                    min={1}
                    onChange={e => setProposeJobModal({ ...proposeJobModal, slots: parseInt(e.target.value) })}
                    style={{ ...styles.input, opacity: proposeJobLoading ? 0.6 : 1 }}
                    placeholder="Slots"
                    disabled={proposeJobLoading}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Does this job incorporate any other concentrations?</label>
                  <div style={styles.radioGroup}>
                    <label className="saf-radio-label" style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="proposeOtherConcentrationsEnabled"
                        checked={!!proposeJobModal.otherConcentrationsEnabled}
                        onChange={() => setProposeJobModal({ ...proposeJobModal, otherConcentrationsEnabled: true, otherConcentrations: proposeJobModal.otherConcentrations || [] })}
                        disabled={proposeJobLoading}
                      />
                      Yes
                    </label>
                    <label className="saf-radio-label" style={styles.radioLabel}>
                      <input
                        type="radio"
                        name="proposeOtherConcentrationsEnabled"
                        checked={!proposeJobModal.otherConcentrationsEnabled}
                        onChange={() => setProposeJobModal({ ...proposeJobModal, otherConcentrationsEnabled: false, otherConcentrations: [] })}
                        disabled={proposeJobLoading}
                      />
                      No
                    </label>
                  </div>
                  {proposeJobModal.otherConcentrationsEnabled && (
                    <div className="saf-checkbox-group" style={styles.checkboxGroup}>
                      <span style={{ fontSize: 14, color: '#b00', marginBottom: 4 }}>Select them:</span>
                      {(proposeJobModal.otherConcentrations || []) && availableConcentrations.filter(c => c.id !== trainingPlan.concentration_id).map(conc => (
                        <label key={conc.id} className="saf-checkbox-label" style={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={(proposeJobModal.otherConcentrations || []).includes(conc.id)}
                            onChange={e => {
                              const checked = e.target.checked;
                              setProposeJobModal(prev => ({
                                ...prev,
                                otherConcentrations: checked
                                  ? [...(prev.otherConcentrations || []), conc.id]
                                  : (prev.otherConcentrations || []).filter(id => id !== conc.id)
                              }));
                            }}
                            disabled={proposeJobLoading}
                          />
                          {conc.title}
                        </label>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: '#b00', marginTop: 4 }}>
                    Including other concentrations helps students get credits in multiple concentrations at once.
                  </div>
                </div>
                {proposeJobError && <div style={styles.error}>{proposeJobError}</div>}
                <button
                  style={{ 
                    ...styles.button, 
                    marginTop: 8,
                    opacity: proposeJobLoading ? 0.6 : 1,
                    cursor: proposeJobLoading ? 'not-allowed' : 'pointer'
                  }}
                  onClick={async () => {
                    if (proposeJobLoading) return; // Prevent double-clicks
                    setProposeJobError('');
                    setProposeJobLoading(true);
                    try {
                      console.log('Proposing job with payload:', {
                        job: proposeJobModal.job.id,
                        feevent_id: eventId,
                        trainingplan: trainingPlan.id,
                        createdby: userData.account_id,
                        slots: typeof proposeJobModal.slots === 'number' ? proposeJobModal.slots : (proposeJobModal.slots ? parseInt(proposeJobModal.slots) : 1),
                        title: proposeJobModal.title,
                        description: proposeJobModal.description,
                        otherconcentrations: Array.isArray(proposeJobModal.otherConcentrationsEnabled && proposeJobModal.otherConcentrations) ? proposeJobModal.otherConcentrations : []
                      });
                      await axios.post('/api/field-experience-events/jobsbyevent', {
                        job: proposeJobModal.job.id,
                        feevent_id: eventId,
                        trainingplan: trainingPlan.id,
                        createdby: userData.account_id,
                        slots: typeof proposeJobModal.slots === 'number' ? proposeJobModal.slots : (proposeJobModal.slots ? parseInt(proposeJobModal.slots) : 1),
                        title: proposeJobModal.title,
                        description: proposeJobModal.description,
                        otherconcentrations: Array.isArray(proposeJobModal.otherConcentrationsEnabled && proposeJobModal.otherConcentrations) ? proposeJobModal.otherConcentrations : []
                      }, {
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                      });
                      setProposeJobModal(null);
                      fetchJobsByEvent();
                    } catch (err) {
                      if (err.response && err.response.status === 400 && err.response.data && err.response.data.error) {
                        setProposeJobError('Failed to propose job: ' + err.response.data.error);
                      } else {
                        setProposeJobError('Failed to propose job.');
                      }
                    } finally {
                      setProposeJobLoading(false);
                    }
                  }}
                  disabled={!proposeJobModal.title || !proposeJobModal.description || !proposeJobModal.slots || proposeJobLoading}
                >
                  {proposeJobLoading ? 'Proposing...' : 'Propose Job'}
                </button>
                <button 
                  style={{ 
                    ...styles.button, 
                    marginLeft: 8,
                    opacity: proposeJobLoading ? 0.6 : 1,
                    cursor: proposeJobLoading ? 'not-allowed' : 'pointer'
                  }} 
                  onClick={() => setProposeJobModal(null)}
                  disabled={proposeJobLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Prevent training plan submission if no jobsbyevent exist */}
          {jobsByEvent.length === 0 && (
            <div style={styles.error}>
              You must propose at least one job for this event before submitting your training plan.
            </div>
          )}
        </div>
      )}

      {/* Edit JobByEvent Modal */}
      {editJobByEvent && (
        <div style={{ background: 'rgba(0,0,0,0.3)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 11000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', padding: 24, borderRadius: 8, minWidth: 320, maxWidth: 480, maxHeight: '80vh', overflowY: 'auto' }}>
            <h4>Edit Proposed Job</h4>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="edit-job-title">Job Title</label>
              <input
                id="edit-job-title"
                type="text"
                value={editJobByEvent.title}
                onChange={e => setEditJobByEvent({ ...editJobByEvent, title: e.target.value })}
                style={styles.input}
                placeholder="Job Title"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="edit-job-description">Job Description</label>
              <textarea
                id="edit-job-description"
                value={editJobByEvent.description}
                onChange={e => setEditJobByEvent({ ...editJobByEvent, description: e.target.value })}
                style={styles.textarea}
                placeholder="Job Description"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="edit-job-slots">Slots (positions for this event)</label>
              <input
                id="edit-job-slots"
                type="number"
                value={typeof editJobByEvent.slots === 'number' ? editJobByEvent.slots : (editJobByEvent.slots ? parseInt(editJobByEvent.slots) : 1)}
                min={1}
                onChange={e => setEditJobByEvent({ ...editJobByEvent, slots: parseInt(e.target.value) })}
                style={styles.input}
                placeholder="Slots"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Does this job incorporate any other concentrations?</label>
              <div style={styles.radioGroup}>
                <label className="saf-radio-label" style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="editOtherConcentrationsEnabled"
                    checked={!!editJobByEvent.otherConcentrationsEnabled}
                    onChange={() => setEditJobByEvent({ ...editJobByEvent, otherConcentrationsEnabled: true })}
                  />
                  Yes
                </label>
                <label className="saf-radio-label" style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="editOtherConcentrationsEnabled"
                    checked={!editJobByEvent.otherConcentrationsEnabled}
                    onChange={() => setEditJobByEvent({ ...editJobByEvent, otherConcentrationsEnabled: false, otherConcentrations: [] })}
                  />
                  No
                </label>
              </div>
              {editJobByEvent.otherConcentrationsEnabled && (
                <div className="saf-checkbox-group" style={styles.checkboxGroup}>
                  <span style={{ fontSize: 14, color: '#b00', marginBottom: 4 }}>Select them:</span>
                  {availableConcentrations.filter(c => c.id !== trainingPlan.concentration_id).map(conc => (
                    <label key={conc.id} className="saf-checkbox-label" style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={editJobByEvent.otherConcentrations && editJobByEvent.otherConcentrations.includes(conc.id)}
                        onChange={e => {
                          const checked = e.target.checked;
                          setEditJobByEvent(prev => ({
                            ...prev,
                            otherConcentrations: checked
                              ? [...(prev.otherConcentrations || []), conc.id]
                              : (prev.otherConcentrations || []).filter(id => id !== conc.id)
                          }));
                        }}
                      />
                      {conc.title}
                    </label>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 12, color: '#b00', marginTop: 4 }}>
                Including other concentrations helps students get credits in multiple concentrations at once.
              </div>
            </div>
            {editJobByEventError && <div style={styles.error}>{editJobByEventError}</div>}
            <button
              style={{ ...styles.button, marginTop: 8 }}
              onClick={async () => {
                setEditJobByEventError('');
                setEditJobByEventSaving(true);
                try {
                  await axios.patch(`/api/field-experience-events/jobsbyevent/${editJobByEvent.id}`, {
                    title: editJobByEvent.title,
                    description: editJobByEvent.description,
                    slots: typeof editJobByEvent.slots === 'number' ? editJobByEvent.slots : (editJobByEvent.slots ? parseInt(editJobByEvent.slots) : 1),
                    otherconcentrations: editJobByEvent.otherConcentrationsEnabled ? (editJobByEvent.otherConcentrations || []) : []
                  }, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                  });
                  setEditJobByEvent(null);
                  fetchJobsByEvent();
                } catch (err) {
                  if (err.response && err.response.data && err.response.data.error) {
                    setEditJobByEventError('Failed to update job: ' + err.response.data.error);
                  } else {
                    setEditJobByEventError('Failed to update job.');
                  }
                } finally {
                  setEditJobByEventSaving(false);
                }
              }}
              disabled={editJobByEventSaving || !editJobByEvent.title || !editJobByEvent.description || !editJobByEvent.slots}
            >
              {editJobByEventSaving ? 'Saving...' : 'Save Changes'}
            </button>
            <button style={{ ...styles.button, marginLeft: 8 }} onClick={() => setEditJobByEvent(null)} disabled={editJobByEventSaving}>Cancel</button>
          </div>
        </div>
      )}

      {/* Create New Job Template Modal */}
      {showJobTemplateForm && (
        <div style={{ background: 'rgba(0,0,0,0.3)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 11000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', padding: 24, borderRadius: 8, minWidth: 400, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}>
            {jobTemplateLoading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Creating Job Template...</div>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>Please wait</div>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h4 style={{ margin: 0 }}>Create New Job Template</h4>
              <button
                onClick={() => {
                  setShowJobTemplateForm(false);
                  setNewJobTemplate({ title: '', description: '', otherConcentrationsEnabled: false, otherConcentrations: [] });
                  setJobTemplateError('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666',
                  padding: 0,
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                disabled={jobTemplateLoading}
              >
                ×
              </button>
            </div>
            {jobTemplateError && <div style={styles.error}>{jobTemplateError}</div>}
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="job-template-title">Job Title</label>
              <input
                id="job-template-title"
                type="text"
                placeholder="Job Title"
                value={newJobTemplate.title}
                onChange={e => setNewJobTemplate({ ...newJobTemplate, title: e.target.value })}
                style={styles.input}
                disabled={jobTemplateLoading}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="job-template-description">Job Description</label>
              <textarea
                id="job-template-description"
                placeholder="Job Description"
                value={newJobTemplate.description}
                onChange={e => setNewJobTemplate({ ...newJobTemplate, description: e.target.value })}
                style={styles.textarea}
                disabled={jobTemplateLoading}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Does this job incorporate any other concentrations?</label>
              <div style={styles.radioGroup}>
                <label className="saf-radio-label" style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="otherConcentrationsEnabled"
                    checked={!!newJobTemplate.otherConcentrationsEnabled}
                    onChange={() => setNewJobTemplate({ ...newJobTemplate, otherConcentrationsEnabled: true })}
                    disabled={jobTemplateLoading}
                  />
                  Yes
                </label>
                <label className="saf-radio-label" style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="otherConcentrationsEnabled"
                    checked={!newJobTemplate.otherConcentrationsEnabled}
                    onChange={() => setNewJobTemplate({ ...newJobTemplate, otherConcentrationsEnabled: false, otherConcentrations: [] })}
                    disabled={jobTemplateLoading}
                  />
                  No
                </label>
              </div>
              {newJobTemplate.otherConcentrationsEnabled && (
                <div className="saf-checkbox-group" style={styles.checkboxGroup}>
                  <span style={{ fontSize: 14, color: '#b00', marginBottom: 4 }}>Select them:</span>
                  {availableConcentrations.filter(c => c.id !== trainingPlan.concentration_id).map(conc => (
                    <label key={conc.id} className="saf-checkbox-label" style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={newJobTemplate.otherConcentrations.includes(conc.id)}
                        onChange={e => {
                          const checked = e.target.checked;
                          setNewJobTemplate(prev => ({
                            ...prev,
                            otherConcentrations: checked
                              ? [...prev.otherConcentrations, conc.id]
                              : prev.otherConcentrations.filter(id => id !== conc.id)
                          }));
                        }}
                        disabled={jobTemplateLoading}
                      />
                      {conc.title}
                    </label>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 12, color: '#b00', marginTop: 4 }}>
                Including other concentrations helps students get credits in multiple concentrations at once.
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                style={{ 
                  ...styles.button,
                  ...styles.saveButton,
                  opacity: jobTemplateLoading ? 0.6 : 1,
                  cursor: jobTemplateLoading ? 'not-allowed' : 'pointer'
                }}
                onClick={async () => {
                  try {
                    setJobTemplateLoading(true);
                    setJobTemplateError('');
                    console.log('Creating job template with payload:', {
                      title: newJobTemplate.title,
                      description: newJobTemplate.description,
                      concentration: trainingPlan.concentration_id,
                      createdby: userData.account_id,
                      otherconcentrations: Array.isArray(newJobTemplate.otherConcentrationsEnabled && newJobTemplate.otherConcentrations) ? newJobTemplate.otherConcentrations : []
                    });
                    await axios.post('/api/field-experience-events/jobs', {
                      title: newJobTemplate.title,
                      description: newJobTemplate.description,
                      concentration: trainingPlan.concentration_id,
                      createdby: userData.account_id,
                      otherconcentrations: Array.isArray(newJobTemplate.otherConcentrationsEnabled && newJobTemplate.otherConcentrations) ? newJobTemplate.otherConcentrations : []
                    }, {
                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                    });
                    setShowJobTemplateForm(false);
                    setNewJobTemplate({ title: '', description: '', otherConcentrationsEnabled: false, otherConcentrations: [] });
                    setJobTemplateError('');
                    fetchJobTemplates();
                  } catch (err) {
                    if (err.response && err.response.status === 400 && err.response.data && err.response.data.error) {
                      setJobTemplateError('Failed to create job template: ' + err.response.data.error);
                    } else {
                      setJobTemplateError('Failed to create job template.');
                    }
                  } finally {
                    setJobTemplateLoading(false);
                  }
                }}
                disabled={!newJobTemplate.title || !newJobTemplate.description || jobTemplateLoading}
              >
                {jobTemplateLoading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Training Plans Table Section */}
      {showTrainingPlansTable && (
        <div id="training-plans-section" style={styles.compactTrainingPlanSection}>
          {trainingPlansLoading ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#666', fontSize: '0.875rem' }}>
              <LoadingSpinner />
              <span style={{ marginLeft: '0.5rem' }}>Loading...</span>
            </div>
          ) : trainingPlansError ? (
            <div style={{ ...styles.error, padding: '0.75rem', fontSize: '0.875rem' }}>{trainingPlansError}</div>
          ) : trainingPlansList.length === 0 ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#999', fontSize: '0.875rem' }}>
              No training plans submitted yet.
            </div>
          ) : (
            <div style={styles.compactTrainingPlansContainer}>
              {/* Group by concentration */}
              {Object.entries(
                trainingPlansList.reduce((groups, plan) => {
                  const concentration = plan.concentration_title;
                  if (!groups[concentration]) {
                    groups[concentration] = [];
                  }
                  groups[concentration].push(plan);
                  return groups;
                }, {})
              ).map(([concentration, plans]) => (
                <div key={concentration} style={styles.compactConcentrationGroup}>
                  <div style={styles.compactConcentrationHeader}>{concentration}</div>
                  {plans.map((plan) => (
                    <div
                      key={plan.id}
                      className="compact-training-plan-row"
                      style={styles.compactTrainingPlanRow}
                      onClick={() => handleTrainingPlanRowClick(plan)}
                    >
                      <div style={styles.compactPlanInfo}>
                        <span style={styles.compactPlanName}>
                          {plan.submitter_first_name} {plan.submitter_last_name}
                        </span>
                        <span style={styles.compactPlanMeta}>
                          {new Date(plan.updateddate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {' · '}
                          {new Date(plan.updateddate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          {plan.revision > 0 && ` · Rev ${plan.revision}`}
                        </span>
                      </div>
                      <button
                        className="compact-view-button"
                        style={styles.compactViewButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTrainingPlanRowClick(plan);
                        }}
                      >
                        View
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Training Plan Modal */}
      {showTrainingPlanModal && selectedTrainingPlan && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 11000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowTrainingPlanModal(false)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ×
            </button>

            <h2>Training Plan - {selectedTrainingPlan.concentration_title}</h2>
            <p><strong>Submitted by:</strong> {selectedTrainingPlan.submitter_first_name} {selectedTrainingPlan.submitter_last_name}</p>
            <p><strong>Submitted on:</strong> {new Date(selectedTrainingPlan.updateddate).toLocaleDateString()} at {new Date(selectedTrainingPlan.updateddate).toLocaleTimeString()}</p>
            <p><strong>Revision:</strong> {selectedTrainingPlan.revision || 0}</p>

            {selectedTrainingPlan.link && (
              <div style={{ marginBottom: '1rem' }}>
                <a
                  href={selectedTrainingPlan.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.link}
                >
                  View Training Plan PDF
                </a>
              </div>
            )}

            <h3>Proposed Jobs</h3>
            {jobsByEventLoading ? (
              <div>Loading proposed jobs...</div>
            ) : jobsByEventError ? (
              <div style={styles.error}>{jobsByEventError}</div>
            ) : jobsByEventForModal.length === 0 ? (
              <div>No jobs proposed for this training plan.</div>
            ) : (
              <ol>
                {jobsByEventForModal.map(jbe => {
                  // Map other concentration IDs to titles
                  const otherConcentrationTitles = Array.isArray(jbe.otherconcentrations) && jbe.otherconcentrations.length > 0
                    ? jbe.otherconcentrations
                      .map(concId => availableConcentrations.find(c => c.id === concId)?.title)
                      .filter(title => title) // Remove any undefined titles
                    : [];

                  return (
                    <li key={jbe.id} style={{ marginBottom: 8 }}>
                      {jbe.status === 'event' && mode === 'coordinator' && (
                        <button
                          style={{
                            ...styles.button,
                            padding: '4px 8px',
                            fontSize: '12px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: 8
                          }}
                          onClick={() => handleUnconfirmJob(jbe.id)}
                        >
                          Unconfirm
                        </button>
                      )}
                      {jbe.status === 'event' && mode !== 'coordinator' && (
                        <span style={{ color: '#28a745', marginRight: '8px' }}>✅Confirmed</span>
                      )}
                      {jbe.status === 'trainingPlan' && mode === 'coordinator' && (
                        <button
                          style={{
                            ...styles.button,
                            padding: '4px 8px',
                            fontSize: '12px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: 8
                          }}
                          onClick={() => handleConfirmJob(jbe.id)}
                        >
                          Confirm
                        </button>
                      )}
                      <strong>{jbe.title}</strong> (<b>Slots:</b> {jbe.slots})
                      {otherConcentrationTitles.length > 0 && (
                        <span style={{ color: '#666', fontStyle: 'italic' }}>
                          {' '}(<b>Other concentrations:</b> {otherConcentrationTitles.join(', ')})
                        </span>
                      )}
                      {' '}- {jbe.description}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      )}

      {/* Sticky Save/Submit SAF Button */}
      {mode !== 'trainingplan' && canEdit && !isTrainingPlansInView && hasUnsavedChanges() && (
        <button
          type="submit"
          form="saf-form"
          disabled={saving}
          className="save-saf-button-entering"
          style={{
            ...styles.stickyButton,
            ...(event && event.stage !== 'request' ? styles.saveButton : styles.submitButton)
          }}
        >
          {saving ? 'Submitting...' : (event && event.stage !== 'request' ? 'Save SAF' : 'Submit SAF')}
        </button>
      )}

      {/* Jobs Notification Bubble */}
      {showJobsNotification && (
        <div style={styles.jobsNotificationBubble}>
          Jobs are not affected by the save button
        </div>
      )}

      {/* Decision Buttons for SAF Stage */}
      {event.stage === 'saf' && mode === 'coordinator' && reviewer && reviewer.involved && (
        <div style={{ marginTop: 12, marginBottom: 12, textAlign: 'center' }}>
          <p style={{ fontSize: '0.82rem', marginBottom: 8 }}>
            {reviewer.safdecision 
              ? 'You can change your decision:' 
              : 'Please make your decision on this saf:'
            }
          </p>
          {confirmedJobs.length === 0 && !reviewer.safdecision && (
            <p style={{ fontSize: '0.75rem', color: '#dc3545', marginBottom: 8 }}>
              Must confirm at least 1 job before approving.
            </p>
          )}
          {confirmedJobsLoading && (
            <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 8 }}>
              Loading job confirmation status...
            </p>
          )}
          <button
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: 5,
              padding: '0.4rem 1rem',
              fontSize: '0.78rem',
              marginRight: 10,
              cursor: (event.stage === 'official' || reviewer.safdecision === 'approved' || confirmedJobs.length === 0 || confirmedJobsLoading) ? 'not-allowed' : 'pointer',
              opacity: (event.stage === 'official' || reviewer.safdecision === 'approved' || confirmedJobs.length === 0 || confirmedJobsLoading) ? 0.6 : 1
            }}
            disabled={event.stage === 'official' || reviewer.safdecision === 'approved' || confirmedJobs.length === 0 || confirmedJobsLoading}
            onClick={async () => {
              try {
                setActionOverlay(true);
                // Check if there are confirmed jobs before allowing approval
                if (confirmedJobs.length === 0) {
                  setError('Must confirm at least 1 job before approving.');
                  return;
                }
                
                const token = localStorage.getItem('token');
                const eventId = event.id;
                const payload = {
                  decision: 'approved',
                  forceApprove: false,
                  expectedUpdatedDate: reviewer.updateddate,
                  expectedEventUpdatedDate: event.updateddate
                };
                await axios.patch(`/api/field-experience-events/invitations/${eventId}/decision`,
                  payload,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                setSuccess('SAF approved.');
                setTimeout(() => window.location.reload(), 1000);
              } catch (err) {
                setError('Failed to approve SAF.');
              } finally {
                setActionOverlay(false);
              }
            }}
          >
            Approve
          </button>
          <button
            style={{
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: 5,
              padding: '0.4rem 1rem',
              fontSize: '0.78rem',
              cursor: (event.stage === 'official' || reviewer.safdecision === 'denied') ? 'not-allowed' : 'pointer',
              opacity: (event.stage === 'official' || reviewer.safdecision === 'denied') ? 0.6 : 1
            }}
            disabled={event.stage === 'official' || reviewer.safdecision === 'denied'}
            onClick={async () => {
              try {
                setActionOverlay(true);
                const token = localStorage.getItem('token');
                const eventId = event.id;
                const payload = {
                  decision: 'denied',
                  forceApprove: false,
                  expectedUpdatedDate: reviewer.updateddate,
                  expectedEventUpdatedDate: event.updateddate
                };
                await axios.patch(`/api/field-experience-events/invitations/${eventId}/decision`,
                  payload,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                setSuccess('SAF denied.');
                setTimeout(() => window.location.reload(), 1000);
              } catch (err) {
                setError('Failed to deny SAF.');
              } finally {
                setActionOverlay(false);
              }
            }}
          >
            Deny
          </button>
        </div>
      )}

      {/* Force Approve Section for SAF Stage */}
      {event.stage === 'saf' && mode === 'coordinator' && reviewer && reviewer.involved && canForceApprove() && (
        <div style={{ 
          marginTop: 24, 
          marginBottom: 24, 
          textAlign: 'center',
          padding: '1rem',
          backgroundColor: '#e3f2fd',
          borderRadius: '4px',
          border: '1px solid #bbdefb'
        }}>
          <p style={{ 
            fontSize: '1rem', 
            color: '#1976d2', 
            marginBottom: '1rem' 
          }}>
            You have made 1 of the required 2 approvals. You may force-approve to immediately approve this stage.
          </p>
          <button
            style={{
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: 5,
              padding: '0.4rem 1rem',
              fontSize: '0.78rem',
              cursor: 'pointer'
            }}
            onClick={async () => {
              try {
                setActionOverlay(true);
                // Check if there are confirmed jobs before allowing force approval
                if (confirmedJobs.length === 0) {
                  setError('Must confirm at least 1 job before approving.');
                  return;
                }
                
                const token = localStorage.getItem('token');
                const eventId = event.id;
                const payload = {
                  decision: 'approved',
                  forceApprove: true,
                  expectedUpdatedDate: reviewer.updateddate,
                  expectedEventUpdatedDate: event.updateddate
                };
                await axios.patch(`/api/field-experience-events/invitations/${eventId}/decision`,
                  payload,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                setSuccess('SAF force approved.');
                setTimeout(() => window.location.reload(), 1000);
              } catch (err) {
                setError('Failed to force approve SAF.');
              } finally {
                setActionOverlay(false);
              }
            }}
          >
            Force Approve
          </button>
        </div>
      )}

      {/* Coordinator Decision History for SAF Stage */}
      {event.stage === 'saf' && mode === 'coordinator' && reviewer && reviewer.involved && event.allCoordinatorDecisions && (
        <div style={{ marginTop: 24, marginBottom: 24, ...styles.decisionHistory }}>
          <h4>Coordinator Decisions:</h4>
          {event.allCoordinatorDecisions.length > 0 ? (
            event.allCoordinatorDecisions.map((coordinator, index) => {
              const decision = coordinator.safdecision;
              const decisionHistory = coordinator.safdecisionhistory;
              return (
                <div key={index} style={styles.historyEntry}>
                  <div style={styles.historyInfo}>
                    <span style={styles.historyAction}>
                      {decision || 'No decision yet'}
                    </span>
                    <span style={styles.historyStage}> SAF</span>
                    <span style={styles.historyCoordinator}>
                      {coordinator.first_name} {coordinator.last_name}
                    </span>
                  </div>
                  <span style={styles.historyTimestamp}>
                    {decision && decisionHistory && decisionHistory.length > 0
                      ? (decisionHistory[decisionHistory.length - 1].timestamp)
                      : ''
                    }
                  </span>
                </div>
              );
            })
          ) : (
            <p>No coordinators are involved yet.</p>
          )}
        </div>
      )}

      {/* Create Official Partner Modal */}
      {showCreatePartnerModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>Create New Official Partner</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Title *</label>
              <input
                type="text"
                value={newPartnerData.title}
                onChange={(e) => setNewPartnerData({ ...newPartnerData, title: e.target.value })}
                style={styles.input}
                placeholder="Enter partner title"
                disabled={creatingPartner}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Display Name</label>
              <input
                type="text"
                value={newPartnerData.displayname}
                onChange={(e) => setNewPartnerData({ ...newPartnerData, displayname: e.target.value })}
                style={styles.input}
                placeholder="Enter display name (optional)"
                disabled={creatingPartner}
              />
            </div>
            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={handleCreateOfficialPartner}
                style={styles.createButton}
                disabled={creatingPartner || !newPartnerData.title.trim()}
              >
                {creatingPartner ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreatePartnerModal(false);
                  setNewPartnerData({ title: '', displayname: '' });
                }}
                style={styles.cancelButton}
                disabled={creatingPartner}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {actionOverlay && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255,255,255,0.8)',
          zIndex: 11001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '960px',
    margin: '0 auto',
    padding: '0.75rem 0.75rem 0.75rem 2.5rem',
    backgroundColor: '#f8f9fa',
    minHeight: '100vh'
  },
  sidebar: {
    position: 'fixed',
    top: '72px',
    left: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    zIndex: 10001,
    width: '34px',
    overflow: 'visible'
  },
  sidebarButton: {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'none'
  },
  title: {
    fontSize: '1.15rem',
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: '0.75rem',
    textAlign: 'center'
  },
  form: {
    backgroundColor: 'transparent',
    padding: '0.25rem 0',
    borderRadius: '6px',
    boxShadow: 'none'
  },
  formRow: {
    display: 'grid',
    gap: '0.35rem',
    marginBottom: '0.45rem'
  },
  formGroup: {
    marginBottom: '0.45rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontWeight: 600,
    color: '#64748b',
    fontSize: '0.68rem',
    textTransform: 'uppercase',
    letterSpacing: '0.02em'
  },
  input: {
    width: '100%',
    padding: '0.32rem 0.5rem',
    border: '1px solid transparent',
    borderRadius: '5px',
    fontSize: '0.78rem',
    outline: 'none',
    minHeight: '28px'
  },
  textarea: {
    width: '100%',
    padding: '0.32rem 0.5rem',
    border: '1px solid transparent',
    borderRadius: '5px',
    fontSize: '0.78rem',
    resize: 'vertical',
    outline: 'none',
    lineHeight: 1.4
  },
  select: {
    width: '100%',
    padding: '0.32rem 0.5rem',
    border: '1px solid transparent',
    borderRadius: '5px',
    fontSize: '0.78rem',
    outline: 'none',
    minHeight: '28px'
  },
  radioGroup: {
    display: 'flex',
    gap: '0.4rem',
    marginBottom: '0.15rem'
  },
  radioLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#475569',
    lineHeight: 1.3,
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  nestedCheckboxes: {
    marginLeft: '0.75rem',
    marginTop: '2px',
    paddingLeft: '8px',
    borderLeft: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  checkboxLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#475569',
    lineHeight: 1.3,
    padding: '1px 0',
  },
  buttonGroup: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'center',
    marginTop: '0.5rem'
  },
  button: {
    padding: '0.4rem 0.85rem',
    border: 'none',
    borderRadius: '5px',
    fontSize: '0.78rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '96px',
    lineHeight: 1.3
  },
  submitButton: {
    backgroundColor: '#28a745',
    color: 'white'
  },
  cancelButton: {
    backgroundColor: '#6c757d',
    color: 'white'
  },
  error: {
    backgroundColor: '#f8fafc',
    color: '#64748b',
    padding: '0.5rem 0.65rem',
    borderRadius: '6px',
    marginBottom: '0.5rem',
    border: '1px solid #e2e8f0',
    fontSize: '0.78rem'
  },
  success: {
    backgroundColor: '#ecfdf5',
    color: '#047857',
    padding: '0.5rem 0.65rem',
    borderRadius: '6px',
    marginBottom: '0.5rem',
    border: '1px solid #bbf7d0',
    fontSize: '0.78rem'
  },
  readOnlyBanner: {
    backgroundColor: '#f8fafc',
    color: '#475569',
    padding: '0.5rem 0.65rem',
    borderRadius: '6px',
    marginBottom: '0.5rem',
    border: '1px solid #e2e8f0',
    fontSize: '0.78rem',
  },
  readOnlyFieldset: {
    border: 'none',
    margin: 0,
    padding: 0,
    minWidth: 0,
  },
  territoryItem: {
    marginBottom: '0.5rem'
  },
  territorySchools: {
    marginLeft: '1.5rem',
    marginTop: '0.25rem'
  },
  schoolList: {
    listStyleType: 'none',
    paddingLeft: '0',
    marginTop: '0.25rem'
  },
  schoolListItem: {
    marginBottom: '0.15rem',
    fontSize: '0.75rem',
    color: '#64748b'
  },
  registrationLimitGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    marginBottom: '0.25rem'
  },
  trainingPlanSection: {
    marginTop: '0.75rem',
    padding: '0.75rem',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    boxShadow: 'none'
  },
  compactTrainingPlanSection: {
    marginTop: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#fafbfc',
    borderRadius: '6px',
    border: '1px solid #e2e8f0'
  },
  compactTrainingPlansContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem'
  },
  compactConcentrationGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem'
  },
  compactConcentrationHeader: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#667085',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.25rem',
    paddingBottom: '0.25rem',
    borderBottom: '1px solid #e3e8f0'
  },
  compactTrainingPlanRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.35rem 0.5rem',
    backgroundColor: 'white',
    borderRadius: '5px',
    border: '1px solid #e2e8f0',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    gap: '0.5rem'
  },
  compactPlanInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    flex: 1,
    minWidth: 0
  },
  compactPlanName: {
    fontSize: '0.78rem',
    fontWeight: 500,
    color: '#334155',
    lineHeight: 1.3
  },
  compactPlanMeta: {
    fontSize: '0.68rem',
    color: '#64748b',
    lineHeight: 1.2
  },
  compactViewButton: {
    padding: '0.2rem 0.55rem',
    fontSize: '0.68rem',
    fontWeight: '500',
    color: '#007bff',
    backgroundColor: 'transparent',
    border: '1px solid #007bff',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  trainingPlanDivider: {
    height: '1px',
    backgroundColor: '#e2e8f0',
    marginBottom: '0.75rem'
  },
  trainingPlanLoading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem'
  },
  trainingPlanContent: {
    maxWidth: '800px',
    margin: '0 auto'
  },
  trainingPlanHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    marginBottom: '0.5rem'
  },
  trainingPlanTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#334155',
    marginBottom: '0.5rem'
  },
  trainingPlanSubtext: {
    fontSize: '0.75rem',
    color: '#64748b',
    marginTop: '-0.15rem',
    marginBottom: '0.5rem'
  },
  revisionMeta: {
    fontSize: '0.68rem',
    color: '#64748b',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '999px',
    padding: '2px 8px',
    whiteSpace: 'nowrap'
  },
  statusPill: {
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'capitalize',
    border: '1px solid transparent',
    whiteSpace: 'nowrap'
  },
  statusPillBuilding: {
    backgroundColor: '#FEF3C7', // amber-100
    color: '#92400E', // amber-800
    borderColor: '#FCD34D' // amber-300
  },
  statusPillSubmitted: {
    backgroundColor: '#DCFCE7', // green-100
    color: '#166534', // green-800
    borderColor: '#86EFAC' // green-300
  },
  trainingPlanStatus: {
    fontSize: '0.78rem',
    fontWeight: 500,
    color: '#64748b',
    marginBottom: '0.65rem',
    padding: '0.35rem 0.5rem',
    backgroundColor: '#f8fafc',
    borderRadius: '5px'
  },
  trainingPlanUpload: {
    marginBottom: '0.75rem'
  },
  trainingPlanSubmitted: {
    marginBottom: '0.75rem'
  },
  jobsTwoColContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.65rem',
    marginTop: '0.75rem'
  },
  jobsColumn: {
    minWidth: 0,
    backgroundColor: '#fafbfc',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0'
  },
  proposedColumn: {
    minWidth: 0,
    backgroundColor: '#f8fafc',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0'
  },
  columnHeaderWithAction: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.45rem',
    paddingBottom: '0.35rem',
    borderBottom: '1px solid #e2e8f0'
  },
  columnHeader: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#475569',
    marginTop: 0,
    marginBottom: '0.45rem',
    paddingBottom: '0.35rem',
    borderBottom: '1px solid #e2e8f0'
  },
  createButtonIcon: {
    width: '24px',
    height: '24px',
    padding: 0,
    fontSize: '1rem',
    fontWeight: '600',
    color: '#475569',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1
  },
  createButton: {
    padding: '0.35rem 0.65rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#475569',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '5px',
    cursor: 'pointer',
    marginBottom: '0.45rem',
    transition: 'all 0.15s ease',
    boxShadow: 'none'
  },
  emptyStateText: {
    fontSize: '0.75rem',
    color: '#64748b',
    marginBottom: '0.45rem'
  },
  jobList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem'
  },
  jobListItem: {
    margin: 0
  },
  jobItemContent: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '0.5rem 0.6rem',
    transition: 'all 0.15s ease'
  },
  jobItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.25rem',
    gap: '0.4rem'
  },
  jobItemTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flex: 1
  },
  jobTitle: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#1e293b',
    lineHeight: 1.35
  },
  jobDescription: {
    fontSize: '0.72rem',
    color: '#64748b',
    lineHeight: 1.4,
    marginBottom: '0.25rem'
  },
  jobMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    fontSize: '0.8125rem',
    color: '#64748b'
  },
  jobMetaLabel: {
    fontWeight: '600',
    color: '#475569'
  },
  jobMetaItem: {
    fontSize: '0.8125rem',
    color: '#64748b',
    marginRight: '0.75rem'
  },
  proposeButton: {
    padding: '0.25rem 0.55rem',
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#007bff',
    backgroundColor: 'transparent',
    border: '1px solid #007bff',
    borderRadius: '5px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flexShrink: 0
  },
  confirmedBadge: {
    fontSize: '0.65rem',
    fontWeight: 600,
    color: '#15803d',
    backgroundColor: '#dcfce7',
    padding: '0.15rem 0.4rem',
    borderRadius: '4px',
    border: '1px solid #bbf7d0'
  },
  jobItemActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexShrink: 0
  },
  iconButton: {
    padding: '0.25rem',
    fontSize: '0.9375rem',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    transition: 'opacity 0.2s ease'
  },
  actionButton: {
    padding: '0.25rem 0.55rem',
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: '#dc3545',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  confirmButton: {
    backgroundColor: '#28a745'
  },
  trainingPlanSubmittedRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  submittedTile: {
    flex: '0 0 180px'
  },
  submittedReplace: {
    flex: '1 1 260px'
  },
  uploaderRow: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    alignItems: 'center',
    gap: '0.45rem',
    padding: '0.45rem',
    backgroundColor: '#ffffff',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    boxShadow: 'none'
  },
  uploadButton: {
    padding: '0.35rem 0.65rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#0f172a',
    backgroundColor: '#F1F5F9',
    border: '1px solid #E2E8F0',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  fileName: {
    fontSize: '0.75rem',
    color: '#64748b',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  uploaderSubmitButton: {
    minWidth: '88px'
  },
  uploaderHint: {
    marginTop: '0.25rem',
    fontSize: '0.68rem',
    color: '#94a3b8'
  },
  replaceHint: {
    marginTop: '0.25rem',
    fontSize: '0.72rem',
    color: '#64748b',
    fontStyle: 'italic'
  },
  trainingPlanLink: {
    marginBottom: '0.65rem',
    padding: '0.5rem',
    backgroundColor: '#f8fafc',
    borderRadius: '5px'
  },
  pdfLinkTile: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.35rem',
    textDecoration: 'none',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    padding: '8px',
    width: '130px',
    boxShadow: 'none'
  },
  pdfIcon: {
    fontSize: '2.5rem'
  },
  pdfCaption: {
    fontSize: '0.75rem',
    color: '#334155',
    fontWeight: 600,
    textAlign: 'center'
  },
  trainingPlanReplace: {
    marginTop: '0.65rem',
    padding: '0.5rem',
    border: 'none',
    borderRadius: '5px'
  },
  fileInput: {
    width: '100%',
    padding: '0.32rem 0.5rem',
    border: '1px solid #e2e8f0',
    borderRadius: '5px',
    marginBottom: '0.35rem',
    fontSize: '0.78rem'
  },
  fileInfo: {
    marginBottom: '0.45rem',
    fontSize: '0.75rem',
    color: '#64748b',
    fontStyle: 'italic'
  },
  link: {
    color: '#007bff',
    textDecoration: 'none'
  },
  versionConflictError: {
    backgroundColor: '#f8fafc',
    color: '#64748b',
    padding: '0.5rem 0.65rem',
    borderRadius: '6px',
    marginBottom: '0.5rem',
    border: '1px solid #e2e8f0',
    fontWeight: 600,
    fontSize: '0.78rem'
  },
  decisionHistory: {
    border: '1px solid #e2e8f0',
    padding: '0.5rem',
    borderRadius: '6px',
    backgroundColor: '#f8fafc'
  },
  historyEntry: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.25rem'
  },
  historyInfo: {
    display: 'flex',
    flexDirection: 'column'
  },
  historyAction: {
    fontSize: '0.78rem',
    fontWeight: 500,
    color: '#334155'
  },
  historyStage: {
    fontSize: '0.72rem',
    color: '#64748b'
  },
  historyCoordinator: {
    fontSize: '0.72rem',
    color: '#64748b'
  },
  historyTimestamp: {
    fontSize: '0.68rem',
    color: '#94a3b8'
  },
  saveButton: {
    backgroundColor: '#007bff',
    color: 'white'
  },
  stickyButton: {
    position: 'fixed',
    bottom: '16px',
    right: '16px',
    padding: '0.45rem 0.9rem',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'box-shadow 0.2s ease, transform 0.15s ease',
    minWidth: '110px',
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12)',
    zIndex: 1000
  },
  jobsNotificationBubble: {
    position: 'fixed',
    bottom: '64px',
    right: '16px',
    padding: '5px 9px',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '5px',
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)',
    fontSize: '0.68rem',
    fontWeight: 600,
    color: '#dc3545',
    whiteSpace: 'nowrap',
    zIndex: 1001,
    animation: 'fadeInOut 3s ease-in-out',
    pointerEvents: 'none'
  },
  partnersContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    marginTop: '0.25rem'
  },
  partnerItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.4rem 0.5rem',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '5px'
  },
  partnerActions: {
    display: 'flex',
    gap: '0.4rem',
    alignItems: 'center'
  },
  partnerSelect: {
    flex: 1,
    padding: '0.32rem 0.5rem',
    border: '1px solid #e2e8f0',
    borderRadius: '5px',
    fontSize: '0.78rem',
    backgroundColor: '#fafbfc',
    minHeight: '28px'
  },
  addPartnerButton: {
    padding: '0.35rem 0.65rem',
    backgroundColor: 'transparent',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '5px',
    fontSize: '0.75rem',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, color 0.15s ease'
  },
  removeButton: {
    background: 'none',
    border: 'none',
    color: '#dc3545',
    fontSize: '1.1rem',
    cursor: 'pointer',
    padding: '0 0.35rem',
    lineHeight: 1
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 11002
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '0.85rem',
    maxWidth: '440px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  modalTitle: {
    fontSize: '0.95rem',
    marginBottom: '0.65rem',
    color: '#334155',
    fontWeight: 600
  },
  modalActions: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
    marginTop: '0.65rem'
  },
  createButton: {
    padding: '0.4rem 0.85rem',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '0.78rem',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease'
  },
  cancelButton: {
    padding: '0.4rem 0.85rem',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '0.78rem',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease'
  }
};

export default SAFForm; 